import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  Search, 
  TrendingUp, 
  Link2,
  Globe,
  Target,
  BarChart3,
  ArrowLeft,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle2, CheckCircle,
  ExternalLink,
  Shield,
  Smartphone,
  Zap,
  FileText,
  Settings,
  XCircle,
  Clock,
  Activity,
  Eye,
  Lock,
  Unlock,
  Image,
  Link,
  FileDown,
  History,
  Save,
  Sparkles,
  ArrowRight,
  AlertCircle,
  Lightbulb,
  ChevronRight,
  Home
} from "lucide-react";
import axios from "../lib/axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ToolSEOContent from "@/components/ToolSEOContent";
import RealSEODashboard from "@/components/RealSEODashboard";
import WhatsAppChat from "@/components/WhatsAppChat";
import RelatedTools from "@/components/RelatedTools";
import { getRelatedTools } from "@/data/toolsData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// CSV Export utility function
const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) {
    toast.error("No data to export");
    return;
  }

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Handle values with commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
  toast.success(`Exported ${data.length} rows to CSV`);
};

// PDF Export utility function
const exportToPDF = (auditData) => {
  // Create printable HTML content
  const printWindow = window.open('', '_blank');
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Site Audit Report - ${auditData.domain}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
        h1 { color: #7c3aed; border-bottom: 2px solid #7c3aed; padding-bottom: 10px; }
        h2 { color: #4c1d95; margin-top: 30px; }
        .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .score-box { display: inline-block; padding: 15px 25px; border-radius: 10px; margin: 5px; text-align: center; }
        .score-good { background: #dcfce7; color: #166534; }
        .score-warning { background: #fef3c7; color: #92400e; }
        .score-bad { background: #fee2e2; color: #991b1b; }
        .scores-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 20px 0; }
        table { width: 100%; border-collapse: collapse; margin: 15px 0; }
        th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb; }
        th { background: #f3f4f6; }
        .severity-critical { color: #dc2626; font-weight: bold; }
        .severity-high { color: #ea580c; font-weight: bold; }
        .severity-medium { color: #ca8a04; }
        .severity-low { color: #65a30d; }
        .status-good { color: #16a34a; }
        .status-warning { color: #d97706; }
        .status-error { color: #dc2626; }
        .metric-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
        .section { margin: 25px 0; padding: 20px; background: #f9fafb; border-radius: 10px; }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <h1>🔍 Site Audit Report</h1>
          <p><strong>URL:</strong> ${auditData.url}</p>
          <p><strong>Date:</strong> ${new Date(auditData.analyzed_at).toLocaleDateString()}</p>
        </div>
      </div>

      <div class="scores-grid">
        <div class="score-box ${auditData.overall_score >= 70 ? 'score-good' : auditData.overall_score >= 50 ? 'score-warning' : 'score-bad'}">
          <div style="font-size: 28px; font-weight: bold;">${auditData.overall_score}</div>
          <div>Overall Score</div>
        </div>
        <div class="score-box ${auditData.seo_score >= 70 ? 'score-good' : auditData.seo_score >= 50 ? 'score-warning' : 'score-bad'}">
          <div style="font-size: 28px; font-weight: bold;">${auditData.seo_score}</div>
          <div>SEO Score</div>
        </div>
        <div class="score-box ${auditData.performance_score >= 70 ? 'score-good' : auditData.performance_score >= 50 ? 'score-warning' : 'score-bad'}">
          <div style="font-size: 28px; font-weight: bold;">${auditData.performance_score}</div>
          <div>Performance</div>
        </div>
        <div class="score-box ${auditData.technical_score >= 70 ? 'score-good' : auditData.technical_score >= 50 ? 'score-warning' : 'score-bad'}">
          <div style="font-size: 28px; font-weight: bold;">${auditData.technical_score}</div>
          <div>Technical</div>
        </div>
        <div class="score-box ${auditData.security_score >= 70 ? 'score-good' : auditData.security_score >= 50 ? 'score-warning' : 'score-bad'}">
          <div style="font-size: 28px; font-weight: bold;">${auditData.security_score}</div>
          <div>Security</div>
        </div>
        <div class="score-box ${auditData.mobile_score >= 70 ? 'score-good' : auditData.mobile_score >= 50 ? 'score-warning' : 'score-bad'}">
          <div style="font-size: 28px; font-weight: bold;">${auditData.mobile_score}</div>
          <div>Mobile</div>
        </div>
      </div>

      <div class="section">
        <h2>📊 Issues Summary</h2>
        <p>Total Issues: <strong>${auditData.issues_summary?.total || 0}</strong></p>
        <p><span class="severity-critical">Critical: ${auditData.issues_summary?.critical || 0}</span> | 
           <span class="severity-high">High: ${auditData.issues_summary?.high || 0}</span> | 
           <span class="severity-medium">Medium: ${auditData.issues_summary?.medium || 0}</span> | 
           <span class="severity-low">Low: ${auditData.issues_summary?.low || 0}</span></p>
      </div>

      <h2>🔴 Issues Found</h2>
      <table>
        <tr><th>Severity</th><th>Category</th><th>Issue</th><th>Recommendation</th></tr>
        ${auditData.issues?.map(issue => `
          <tr>
            <td class="severity-${issue.severity}">${issue.severity.toUpperCase()}</td>
            <td>${issue.category}</td>
            <td>${issue.issue}</td>
            <td>${issue.recommendation}</td>
          </tr>
        `).join('') || '<tr><td colspan="4">No issues found</td></tr>'}
      </table>

      <h2>⚡ Core Web Vitals</h2>
      <table>
        <tr><th>Metric</th><th>Value</th><th>Status</th></tr>
        ${Object.entries(auditData.performance?.core_web_vitals || {}).map(([key, cwv]) => `
          <tr>
            <td>${cwv.label}</td>
            <td>${cwv.value}${cwv.unit}</td>
            <td class="status-${cwv.status}">${cwv.status.toUpperCase()}</td>
          </tr>
        `).join('')}
      </table>

      <h2>📝 On-Page SEO</h2>
      <div class="metric-row"><span>Title</span><span class="status-${auditData.on_page_seo?.title?.status}">${auditData.on_page_seo?.title?.value || 'Missing'} (${auditData.on_page_seo?.title?.length || 0} chars)</span></div>
      <div class="metric-row"><span>Meta Description</span><span class="status-${auditData.on_page_seo?.meta_description?.status}">${auditData.on_page_seo?.meta_description?.length || 0} chars</span></div>
      <div class="metric-row"><span>H1 Tags</span><span class="status-${auditData.on_page_seo?.h1_tags?.status}">${auditData.on_page_seo?.h1_tags?.count || 0} found</span></div>
      <div class="metric-row"><span>Images</span><span class="status-${auditData.on_page_seo?.images?.status}">${auditData.on_page_seo?.images?.without_alt || 0} without alt text</span></div>
      <div class="metric-row"><span>Word Count</span><span class="status-${auditData.on_page_seo?.word_count?.status}">${auditData.on_page_seo?.word_count?.value || 0} words</span></div>

      <h2>🔗 Link Analysis</h2>
      <div class="metric-row"><span>Internal Links</span><span>${auditData.links?.internal || 0}</span></div>
      <div class="metric-row"><span>External Links</span><span>${auditData.links?.external || 0}</span></div>
      <div class="metric-row"><span>Broken Links</span><span>${auditData.links?.broken_links || 0}</span></div>

      <h2>💡 Recommendations</h2>
      ${auditData.recommendations?.map(rec => `
        <div style="margin: 10px 0; padding: 10px; background: #f0fdf4; border-left: 4px solid #22c55e;">
          <strong>${rec.title}</strong>
          <p>${rec.description}</p>
          <small>Estimated Impact: ${rec.estimated_impact}</small>
        </div>
      `).join('') || '<p>No recommendations</p>'}

      <hr style="margin-top: 40px;">
      <p style="text-align: center; color: #6b7280; font-size: 12px;">
        Generated by MarkeeMix Site Audit Tool | ${new Date().toLocaleString()}
      </p>
    </body>
    </html>
  `;
  
  printWindow.document.write(html);
  printWindow.document.close();
  
  // Wait for content to load then print
  printWindow.onload = () => {
    printWindow.print();
  };
  
  toast.success("PDF report generated! Use Ctrl+P to save as PDF");
};

// PDF Export utility function for Keyword Research
const exportKeywordsToPDF = (keywordData, seedKeyword) => {
  const printWindow = window.open('', '_blank');
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Keyword Research Report - ${seedKeyword}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; max-width: 900px; margin: 0 auto; }
        h1 { color: #7c3aed; border-bottom: 2px solid #7c3aed; padding-bottom: 10px; }
        h2 { color: #4c1d95; margin-top: 30px; }
        .header { margin-bottom: 20px; }
        .stats-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin: 20px 0; }
        .stat-box { padding: 15px; border-radius: 8px; text-align: center; }
        .stat-box.purple { background: #f5f3ff; border: 1px solid #c4b5fd; }
        .stat-box.blue { background: #eff6ff; border: 1px solid #93c5fd; }
        .stat-box.green { background: #f0fdf4; border: 1px solid #86efac; }
        .stat-box.orange { background: #fff7ed; border: 1px solid #fdba74; }
        .stat-box.pink { background: #fdf2f8; border: 1px solid #f9a8d4; }
        .stat-value { font-size: 24px; font-weight: bold; color: #4c1d95; }
        .stat-label { font-size: 11px; color: #6b7280; text-transform: uppercase; }
        table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 12px; }
        th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
        th { background: #f3f4f6; font-weight: 600; }
        .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; }
        .badge-green { background: #dcfce7; color: #166534; }
        .badge-yellow { background: #fef3c7; color: #92400e; }
        .badge-red { background: #fee2e2; color: #991b1b; }
        .badge-blue { background: #dbeafe; color: #1e40af; }
        .section { margin: 25px 0; padding: 15px; background: #f9fafb; border-radius: 10px; border: 1px solid #e5e7eb; }
        .serp-features { display: flex; flex-wrap: wrap; gap: 8px; }
        .serp-badge { background: #dbeafe; color: #1e40af; padding: 4px 10px; border-radius: 15px; font-size: 11px; }
        .paa-item { padding: 10px; background: white; border-left: 3px solid #3b82f6; margin: 8px 0; border-radius: 4px; }
        .paa-question { font-weight: 600; color: #1f2937; }
        .paa-answer { font-size: 12px; color: #6b7280; margin-top: 4px; }
        @media print { body { padding: 20px; } .section { break-inside: avoid; } }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>🎯 Keyword Research Report</h1>
        <p><strong>Seed Keyword:</strong> ${seedKeyword}</p>
        <p><strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
      </div>

      <div class="stats-grid">
        <div class="stat-box purple">
          <div class="stat-value">${keywordData.keyword_metrics?.search_volume?.toLocaleString() || 'N/A'}</div>
          <div class="stat-label">Search Volume</div>
        </div>
        <div class="stat-box blue">
          <div class="stat-value">${keywordData.keyword_metrics?.difficulty || 'N/A'}</div>
          <div class="stat-label">${keywordData.keyword_metrics?.difficulty_label || 'Difficulty'}</div>
        </div>
        <div class="stat-box green">
          <div class="stat-value">$${keywordData.keyword_metrics?.cpc || '0'}</div>
          <div class="stat-label">CPC</div>
        </div>
        <div class="stat-box orange">
          <div class="stat-value">${Math.round((keywordData.keyword_metrics?.competition || 0) * 100)}%</div>
          <div class="stat-label">Competition</div>
        </div>
        <div class="stat-box pink">
          <div class="stat-value" style="text-transform: capitalize;">${keywordData.keyword_metrics?.trend || 'N/A'}</div>
          <div class="stat-label">${keywordData.keyword_metrics?.seasonal ? 'Seasonal' : 'Evergreen'}</div>
        </div>
      </div>

      ${keywordData.serp_features?.length > 0 ? `
        <div class="section">
          <h2>🔍 SERP Features</h2>
          <div class="serp-features">
            ${keywordData.serp_features.map(f => `<span class="serp-badge">${f.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}${f.count ? ' (' + f.count + ')' : ''}</span>`).join('')}
          </div>
        </div>
      ` : ''}

      ${keywordData.analysis_summary ? `
        <div class="section">
          <h2>📊 Analysis Summary</h2>
          <p><strong>Ranking Opportunity:</strong> ${keywordData.analysis_summary.ranking_opportunity}</p>
          <p><strong>Featured Snippet Opportunity:</strong> ${keywordData.analysis_summary.featured_snippet_opportunity ? 'Yes' : 'No'}</p>
          <h3>Recommendations:</h3>
          <ul>
            ${keywordData.analysis_summary.recommendations?.map(r => `<li>${r}</li>`).join('') || '<li>No recommendations</li>'}
          </ul>
        </div>
      ` : ''}

      ${keywordData.keyword_planner?.length > 0 ? `
        <h2>📋 Keyword Planner (${keywordData.keyword_planner.length} keywords)</h2>
        <table>
          <tr>
            <th>#</th>
            <th>Keyword</th>
            <th>Volume</th>
            <th>Competition</th>
            <th>CPC</th>
            <th>Intent</th>
          </tr>
          ${keywordData.keyword_planner.slice(0, 20).map((kp, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${kp.keyword}</td>
              <td>${kp.avg_monthly_searches?.toLocaleString() || 'N/A'}</td>
              <td><span class="badge ${kp.competition === 'High' ? 'badge-red' : kp.competition === 'Medium' ? 'badge-yellow' : 'badge-green'}">${kp.competition}</span></td>
              <td>$${kp.cpc}</td>
              <td>${kp.intent || 'info'}</td>
            </tr>
          `).join('')}
        </table>
      ` : ''}

      ${keywordData.people_also_ask?.length > 0 ? `
        <h2>❓ People Also Ask (${keywordData.people_also_ask.length} questions)</h2>
        ${keywordData.people_also_ask.slice(0, 10).map(paa => `
          <div class="paa-item">
            <div class="paa-question">${paa.question}</div>
            <div class="paa-answer">${paa.answer_preview}</div>
          </div>
        `).join('')}
      ` : ''}

      ${keywordData.related_searches?.length > 0 ? `
        <h2>🔗 Related Searches (${keywordData.related_searches.length} keywords)</h2>
        <table>
          <tr>
            <th>#</th>
            <th>Keyword</th>
            <th>Volume</th>
            <th>Trend</th>
          </tr>
          ${keywordData.related_searches.slice(0, 15).map((rs, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${rs.keyword}</td>
              <td>${rs.search_volume?.toLocaleString() || 'N/A'}</td>
              <td><span class="badge ${rs.trend === 'rising' ? 'badge-green' : rs.trend === 'declining' ? 'badge-red' : 'badge-blue'}">${rs.trend === 'rising' ? '↑ Rising' : rs.trend === 'declining' ? '↓ Declining' : '→ Stable'}</span></td>
            </tr>
          `).join('')}
        </table>
      ` : ''}

      ${keywordData.long_tail_keywords?.length > 0 ? `
        <h2>🎯 Long-Tail Keywords (${keywordData.long_tail_keywords.length} keywords)</h2>
        <table>
          <tr>
            <th>#</th>
            <th>Keyword</th>
            <th>Volume</th>
            <th>Difficulty</th>
            <th>CPC</th>
            <th>Opportunity</th>
          </tr>
          ${keywordData.long_tail_keywords.slice(0, 15).map((lt, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${lt.keyword}</td>
              <td>${lt.search_volume?.toLocaleString() || 'N/A'}</td>
              <td><span class="badge ${lt.difficulty < 30 ? 'badge-green' : lt.difficulty < 60 ? 'badge-yellow' : 'badge-red'}">${lt.difficulty} - ${lt.difficulty_label}</span></td>
              <td>$${lt.cpc}</td>
              <td>${lt.opportunity_score}</td>
            </tr>
          `).join('')}
        </table>
      ` : ''}

      ${keywordData.serp_results?.length > 0 ? `
        <h2>🏆 Top SERP Results</h2>
        <table>
          <tr>
            <th>#</th>
            <th>Title</th>
            <th>Domain</th>
            <th>Type</th>
          </tr>
          ${keywordData.serp_results.slice(0, 10).map(sr => `
            <tr>
              <td>${sr.position}</td>
              <td>${sr.title}</td>
              <td>${sr.domain}</td>
              <td>${sr.type}</td>
            </tr>
          `).join('')}
        </table>
      ` : ''}

      <hr style="margin-top: 40px;">
      <p style="text-align: center; color: #6b7280; font-size: 12px;">
        Generated by MarkeeMix Keyword Research Tool | ${new Date().toLocaleString()}
      </p>
    </body>
    </html>
  `;
  
  printWindow.document.write(html);
  printWindow.document.close();
  
  printWindow.onload = () => {
    printWindow.print();
  };
  
  toast.success("Keyword Report PDF generated! Use Ctrl+P to save as PDF");
};

export default function SEOToolsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTool, setActiveTool] = useState("keyword-research");

  // Get tool from URL params (e.g., /tools/seo?tool=backlink-analyzer)
  useEffect(() => {
    const toolFromUrl = searchParams.get('tool');
    if (toolFromUrl) {
      setActiveTool(toolFromUrl);
    }
  }, [searchParams]);

  // Get domain from URL params for auto-analysis
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const domainFromUrl = params.get('domain');
    if (domainFromUrl) {
      setAuditUrl(domainFromUrl);
      setActiveTool("site-audit");
      // Auto-start analysis
      setTimeout(() => handleSiteAudit(domainFromUrl), 500);
    }
  }, []);

  // Keyword Research State
  const [keywordInput, setKeywordInput] = useState("");
  const [keywordResults, setKeywordResults] = useState(null);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [advancedResults, setAdvancedResults] = useState(null);
  const [activeKeywordTab, setActiveKeywordTab] = useState("overview");
  const [usageStatus, setUsageStatus] = useState(null);
  
  // Keyword Autocomplete State
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const autocompleteTimeoutRef = useRef(null);

  // Fetch keyword suggestions as user types
  const fetchKeywordSuggestions = async (query) => {
    if (query.length < 2) {
      setKeywordSuggestions([]);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      // Use Google autocomplete API via our backend
      const response = await axios.get(`${API}/keywords/autocomplete`, {
        params: { query },
        withCredentials: true,
        headers: { 'X-Skip-Auth-Error': 'true' }
      });
      setKeywordSuggestions(response.data.suggestions || []);
    } catch (error) {
      // Fallback to generating suggestions from keyword patterns
      const fallbackSuggestions = [
        `${query} tips`,
        `${query} for beginners`,
        `best ${query}`,
        `how to ${query}`,
        `${query} tutorial`,
        `${query} guide`,
        `${query} examples`,
        `${query} tools`
      ].slice(0, 6);
      setKeywordSuggestions(fallbackSuggestions);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Handle input change with debounced autocomplete
  const handleKeywordInputChange = (value) => {
    setKeywordInput(value);
    setShowSuggestions(true);
    
    // Clear previous timeout
    if (autocompleteTimeoutRef.current) {
      clearTimeout(autocompleteTimeoutRef.current);
    }
    
    // Debounce the API call
    autocompleteTimeoutRef.current = setTimeout(() => {
      fetchKeywordSuggestions(value);
    }, 300);
  };

  // Select a suggestion
  const selectSuggestion = (suggestion) => {
    setKeywordInput(suggestion);
    setShowSuggestions(false);
    setKeywordSuggestions([]);
  };

  // Fetch usage status on mount and after searches
  const fetchUsageStatus = async () => {
    try {
      const response = await axios.get(`${API}/keywords/usage-status`, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      setUsageStatus(response.data);
    } catch (e) {
      console.log("Could not fetch usage status");
    }
  };

  useEffect(() => {
    fetchUsageStatus();
  }, []);

  // Backlink Analyzer State
  const [backlinkUrl, setBacklinkUrl] = useState("");
  const [backlinkResults, setBacklinkResults] = useState(null);
  const [loadingBacklinks, setLoadingBacklinks] = useState(false);

  // Rank Tracking State
  const [trackingDomain, setTrackingDomain] = useState("");
  const [trackingKeywords, setTrackingKeywords] = useState("");
  const [rankingResults, setRankingResults] = useState(null);
  const [loadingRanking, setLoadingRanking] = useState(false);

  // Site Audit State
  const [auditUrl, setAuditUrl] = useState("");
  const [auditResults, setAuditResults] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [activeAuditTab, setActiveAuditTab] = useState("overview");

  // Site Structure State
  const [structureUrl, setStructureUrl] = useState("");
  const [structureResults, setStructureResults] = useState(null);
  const [loadingStructure, setLoadingStructure] = useState(false);

  // Competitor Analysis State
  const [domain1, setDomain1] = useState("");
  const [domain2, setDomain2] = useState("");
  const [domain3, setDomain3] = useState("");
  const [comparisonResults, setComparisonResults] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [competitorTab, setCompetitorTab] = useState("comparison");

  // Audit History State
  const [auditHistory, setAuditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Keyword History State
  const [keywordHistory, setKeywordHistory] = useState([]);
  const [loadingKeywordHistory, setLoadingKeywordHistory] = useState(false);

  // Industry Keywords Engine State
  const [industryForm, setIndustryForm] = useState({
    industry: "",
    city: "",
    target_audience: "",
    business_type: "Local"
  });
  const [industryResults, setIndustryResults] = useState(null);
  const [industryLoading, setIndustryLoading] = useState(false);

  // Keyword Comparison State
  const [compareKeywords, setCompareKeywords] = useState(["", "", ""]);
  const [compareResults, setCompareResults] = useState([]);
  const [loadingCompare, setLoadingCompare] = useState(false);

  // Keyword Clustering State
  const [clusterKeywords, setClusterKeywords] = useState("");
  const [clusterResults, setClusterResults] = useState(null);
  const [loadingCluster, setLoadingCluster] = useState(false);

  // Keyword Clustering Handler
  const handleKeywordClustering = async () => {
    const keywords = clusterKeywords.split('\n').map(k => k.trim()).filter(k => k);
    if (keywords.length < 5) {
      toast.error("Please enter at least 5 keywords (one per line)");
      return;
    }

    setLoadingCluster(true);
    try {
      // Group keywords by common terms
      const clusters = {};
      
      keywords.forEach(kw => {
        const words = kw.toLowerCase().split(/\s+/);
        let assigned = false;
        
        // Try to match with existing clusters
        Object.keys(clusters).forEach(clusterName => {
          const clusterWords = clusterName.toLowerCase().split(/\s+/);
          if (words.some(w => clusterWords.includes(w) || clusterWords.some(cw => w.includes(cw) || cw.includes(w)))) {
            clusters[clusterName].push(kw);
            assigned = true;
          }
        });
        
        // Create new cluster if not assigned
        if (!assigned) {
          const mainWord = words.find(w => w.length > 3) || words[0];
          const clusterName = mainWord.charAt(0).toUpperCase() + mainWord.slice(1);
          if (!clusters[clusterName]) clusters[clusterName] = [];
          clusters[clusterName].push(kw);
        }
      });

      // Merge small clusters
      const mergedClusters = {};
      Object.entries(clusters).forEach(([name, kws]) => {
        if (kws.length >= 2) {
          mergedClusters[name] = kws;
        } else {
          if (!mergedClusters['Other']) mergedClusters['Other'] = [];
          mergedClusters['Other'].push(...kws);
        }
      });

      setClusterResults({
        total_keywords: keywords.length,
        total_clusters: Object.keys(mergedClusters).length,
        clusters: Object.entries(mergedClusters).map(([name, kws]) => ({
          name,
          keywords: kws,
          count: kws.length,
          intent: kws.some(k => k.includes('buy') || k.includes('price')) ? 'Transactional' :
                  kws.some(k => k.includes('how') || k.includes('what')) ? 'Informational' : 'Navigational'
        }))
      });
      
      toast.success(`Created ${Object.keys(mergedClusters).length} keyword clusters!`);
    } catch (error) {
      toast.error("Clustering failed");
    } finally {
      setLoadingCluster(false);
    }
  };

  // Handle keyword comparison
  const handleKeywordComparison = async () => {
    const keywords = compareKeywords.filter(k => k.trim());
    if (keywords.length < 2) {
      toast.error("Please enter at least 2 keywords to compare");
      return;
    }

    setLoadingCompare(true);
    setCompareResults([]);

    try {
      // Fetch data for each keyword in parallel
      const promises = keywords.map(async (kw) => {
        try {
          const response = await axios.post(`${API}/keywords/advanced-research`, {
            keyword: kw.trim()
          }, { withCredentials: true });
          return {
            keyword: kw.trim(),
            success: true,
            data: response.data
          };
        } catch (error) {
          return {
            keyword: kw.trim(),
            success: false,
            error: error.response?.data?.detail || "Failed to fetch data"
          };
        }
      });

      const results = await Promise.all(promises);
      setCompareResults(results);
      
      const successCount = results.filter(r => r.success).length;
      if (successCount > 0) {
        toast.success(`Compared ${successCount} keywords!`);
      } else {
        toast.error("Failed to fetch keyword data");
      }
    } catch (error) {
      toast.error("Comparison failed");
    } finally {
      setLoadingCompare(false);
    }
  };

  const seoTools = [
    {
      id: "ai-seo-toolkit",
      name: "AI SEO Toolkit",
      icon: <Sparkles className="w-5 h-5" />,
      description: "Live SerpAPI keywords + Claude AI grader, meta tags, schema · NEW",
      isLink: true,
      linkTo: "/tools/seo/ai",
      badge: "NEW",
    },
    {
      id: "seo-dashboard",
      name: "SEO Dashboard",
      icon: <BarChart3 className="w-5 h-5" />,
      description: "Complete SEO management dashboard",
      isLink: true,
      linkTo: "/tools/seo/dashboard"
    },
    {
      id: "keyword-research",
      name: "Keyword Research",
      icon: <Target className="w-5 h-5" />,
      description: "Find profitable keywords with search volume & difficulty"
    },
    {
      id: "backlink-analyzer",
      name: "Backlink Analyzer",
      icon: <Link2 className="w-5 h-5" />,
      description: "Analyze backlink profile and domain authority"
    },
    {
      id: "rank-tracking",
      name: "Rank Tracking",
      icon: <TrendingUp className="w-5 h-5" />,
      description: "Track keyword rankings over time"
    },
    {
      id: "site-audit",
      name: "Site Audit",
      icon: <Search className="w-5 h-5" />,
      description: "Complete SEO audit with actionable insights"
    },
    {
      id: "site-structure",
      name: "Site Structure",
      icon: <Globe className="w-5 h-5" />,
      description: "Analyze internal linking structure"
    },
    {
      id: "competitor",
      name: "Competitor Analysis",
      icon: <BarChart3 className="w-5 h-5" />,
      description: "Compare domains + keyword gap analysis"
    },
    {
      id: "audit-history",
      name: "Audit History",
      icon: <History className="w-5 h-5" />,
      description: "View all your past SEO audits"
    },
    {
      id: "keyword-history",
      name: "Keyword History",
      icon: <Clock className="w-5 h-5" />,
      description: "View your past keyword searches"
    },
    {
      id: "keyword-compare",
      name: "Keyword Comparison",
      icon: <BarChart3 className="w-5 h-5" />,
      description: "Compare multiple keywords side-by-side",
      badge: "NEW"
    },
    {
      id: "keyword-cluster",
      name: "Keyword Clustering",
      icon: <Target className="w-5 h-5" />,
      description: "Group related keywords by topic",
      badge: "NEW"
    },
    {
      id: "industry-keywords",
      name: "Industry Keywords Engine",
      icon: <Target className="w-5 h-5" />,
      description: "AI-powered industry SEO blueprint",
      badge: "AI"
    },
    {
      id: "technical-seo",
      name: "Technical SEO Tools",
      icon: <Search className="w-5 h-5" />,
      description: "18+ advanced technical SEO tools",
      isLink: true,
      linkTo: "/tools/seo/technical"
    },
    {
      id: "advanced-seo",
      name: "Advanced SEO Suite",
      icon: <BarChart3 className="w-5 h-5" />,
      description: "Site Audit, Sitemap Generator, Schema, Keyword Gap",
      isLink: true,
      linkTo: "/tools/seo/advanced",
      badge: "NEW"
    },
    {
      id: "real-data",
      name: "Real Data Dashboard",
      icon: <CheckCircle className="w-5 h-5" />,
      description: "PageSpeed, Google Autocomplete - verified real data",
      badge: "REAL"
    }
  ];

  // API Handlers
  const handleKeywordResearch = async () => {
    if (!keywordInput.trim()) {
      toast.error("Please enter a keyword");
      return;
    }

    // Check usage limit before making request
    if (usageStatus && !usageStatus.allowed && usageStatus.remaining <= 0) {
      toast.error(usageStatus.upgrade_message || "Search limit reached. Upgrade to Pro!");
      return;
    }

    setLoadingKeywords(true);
    try {
      // Call advanced research API
      const response = await axios.post(`${API}/keywords/advanced-research`, {
        keyword: keywordInput.trim()
      }, { withCredentials: true });
      setAdvancedResults(response.data);
      setActiveKeywordTab("overview");
      
      // Update usage status after successful search
      fetchUsageStatus();
      
      // Save to search history
      try {
        await axios.post(`${BACKEND_URL}/api/user/search-history`, {
          tool: "Keyword Research",
          query: keywordInput.trim(),
          result_summary: `Volume: ${response.data.overview?.search_volume || 'N/A'}, Difficulty: ${response.data.overview?.keyword_difficulty || 'N/A'}`
        }, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      } catch (e) {}
      
      toast.success("Keyword research complete!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to research keywords";
      // Check if it's a rate limit error
      if (error.response?.status === 429) {
        toast.error(errorMsg);
        fetchUsageStatus(); // Refresh usage status
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoadingKeywords(false);
    }
  };

  const handleBacklinkAnalysis = async () => {
    if (!backlinkUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setLoadingBacklinks(true);
    try {
      const response = await axios.post(`${API}/backlinks/analyze`, {
        url: backlinkUrl.trim()
      }, { withCredentials: true });
      setBacklinkResults(response.data);
      
      // Save to search history
      try {
        await axios.post(`${BACKEND_URL}/api/user/search-history`, {
          tool: "Backlink Analyzer",
          query: backlinkUrl.trim(),
          result_summary: `Found ${response.data.total_backlinks || 0} backlinks`
        }, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      } catch (e) {}
      
      toast.success("Backlink analysis complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze backlinks");
    } finally {
      setLoadingBacklinks(false);
    }
  };

  const handleRankTracking = async () => {
    if (!trackingDomain.trim() || !trackingKeywords.trim()) {
      toast.error("Please enter domain and keywords");
      return;
    }

    const keywords = trackingKeywords.split('\n').filter(k => k.trim());
    if (keywords.length === 0) {
      toast.error("Please enter at least one keyword");
      return;
    }

    setLoadingRanking(true);
    try {
      const response = await axios.post(`${API}/ranking/track`, {
        domain: trackingDomain.trim(),
        keywords
      });
      setRankingResults(response.data);
      toast.success(`Tracking ${response.data.total_keywords} keywords!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to track rankings");
    } finally {
      setLoadingRanking(false);
    }
  };

  const handleSiteAudit = async (urlOverride = null) => {
    const urlToAudit = urlOverride || auditUrl.trim();
    if (!urlToAudit) {
      toast.error("Please enter a URL");
      return;
    }

    setLoadingAudit(true);
    try {
      const response = await axios.post(`${API}/site-audit/advanced`, {
        url: urlToAudit
      }, { withCredentials: true });
      setAuditResults(response.data);
      setActiveAuditTab("overview");
      
      // Save to search history
      try {
        await axios.post(`${BACKEND_URL}/api/user/search-history`, {
          tool: "Site Audit",
          query: urlToAudit,
          result_summary: `Score: ${response.data.overall_score || 0}/100, Issues: ${response.data.issues?.length || 0}`
        }, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      } catch (e) {}
      
      toast.success("Site audit complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to audit site");
    } finally {
      setLoadingAudit(false);
    }
  };

  const handleStructureAnalysis = async () => {
    if (!structureUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setLoadingStructure(true);
    try {
      const response = await axios.post(`${API}/structure/analyze`, {
        url: structureUrl.trim()
      }, { withCredentials: true });
      setStructureResults(response.data);
      
      // Save to search history
      try {
        await axios.post(`${BACKEND_URL}/api/user/search-history`, {
          tool: "Site Structure",
          query: structureUrl.trim(),
          result_summary: `Pages: ${response.data.total_pages || 0}, Links: ${response.data.total_links || 0}`
        }, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      } catch (e) {}
      
      toast.success("Structure analysis complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze structure");
    } finally {
      setLoadingStructure(false);
    }
  };

  const handleCompareDomains = async () => {
    const domains = [domain1, domain2, domain3].filter(d => d.trim());
    
    if (domains.length < 2) {
      toast.error("Please enter at least 2 domains");
      return;
    }

    setLoadingComparison(true);
    try {
      // Use enhanced competitor analysis endpoint
      const response = await axios.post(`${API}/advanced-seo/competitor/full-analysis`, { 
        your_domain: domains[0],
        competitor_domains: domains.slice(1),
        save_analysis: true
      });
      setComparisonResults(response.data);
      toast.success("Competitor analysis complete!");
    } catch (error) {
      // Fallback to basic compare-domains
      try {
        const response = await axios.post(`${API}/compare-domains`, { domains });
        setComparisonResults(response.data);
        toast.success("Comparison complete!");
      } catch (e) {
        toast.error(error.response?.data?.detail || "Failed to compare domains");
      }
    } finally {
      setLoadingComparison(false);
    }
  };

  // Fetch audit history
  const fetchAuditHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/advanced-seo/audit/history?limit=20`, { headers: { 'X-Skip-Auth-Error': 'true' } });
      setAuditHistory(response.data.audits || []);
    } catch (error) {
      // Fallback to /api/reports
      try {
        const response = await axios.get(`${API}/reports?limit=20`, { headers: { 'X-Skip-Auth-Error': 'true' } });
        setAuditHistory(response.data || []);
      } catch (e) {
        console.error("Failed to fetch audit history");
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch keyword search history
  const fetchKeywordHistory = async () => {
    setLoadingKeywordHistory(true);
    try {
      const response = await axios.get(`${API}/keywords/history`, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      setKeywordHistory(response.data.history || []);
    } catch (error) {
      console.error("Failed to fetch keyword history:", error);
      setKeywordHistory([]);
    } finally {
      setLoadingKeywordHistory(false);
    }
  };

  // Delete keyword from history
  const deleteKeywordFromHistory = async (keyword) => {
    try {
      await axios.delete(`${API}/keywords/history/${encodeURIComponent(keyword)}`, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      setKeywordHistory(prev => prev.filter(k => k.keyword !== keyword));
      toast.success("Keyword removed from history");
    } catch (error) {
      toast.error("Failed to delete keyword");
    }
  };

  // Search keyword from history
  const searchFromHistory = (keyword) => {
    setKeyword(keyword);
    setActiveTool("keyword-research");
    handleAdvancedResearch({ preventDefault: () => {} });
  };

  // Load audit history when tab is selected
  useEffect(() => {
    if (activeTool === "audit-history") {
      fetchAuditHistory();
    }
    if (activeTool === "keyword-history") {
      fetchKeywordHistory();
    }
  }, [activeTool]);

  const getDifficultyColor = (difficulty) => {
    if (difficulty < 30) return "bg-green-100 text-green-800";
    if (difficulty < 50) return "bg-yellow-100 text-yellow-800";
    if (difficulty < 70) return "bg-orange-100 text-orange-800";
    return "bg-red-100 text-red-800";
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-blue-600";
    if (score >= 40) return "text-orange-600";
    return "text-red-600";
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-300';
      case 'medium': return 'bg-orange-100 text-orange-800 border-orange-300';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
  };

  // Industry Keywords Engine Handler
  const handleIndustryKeywords = async (e) => {
    e.preventDefault();
    if (!industryForm.industry.trim()) {
      toast.error("Please enter an industry");
      return;
    }

    setIndustryLoading(true);
    setIndustryResults(null);
    
    try {
      const response = await axios.post(`${API}/keywords/industry-engine`, {
        industry: industryForm.industry,
        city: industryForm.city || null,
        target_audience: industryForm.target_audience || null,
        business_type: industryForm.business_type
      }, { withCredentials: true });
      
      setIndustryResults(response.data);
      toast.success("Industry keywords generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate keywords");
    } finally {
      setIndustryLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm mb-4" aria-label="Breadcrumb">
          <RouterLink to="/" className="flex items-center gap-1 text-gray-500 hover:text-emerald-600 transition-colors">
            <Home className="w-4 h-4" />
            <span>Home</span>
          </RouterLink>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <RouterLink to="/tools" className="text-gray-500 hover:text-emerald-600 transition-colors">
            Tools
          </RouterLink>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900 font-medium">SEO Tools</span>
        </nav>

        {/* Page Title — Premium */}
        <div className="mb-10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#111111] rounded-2xl flex items-center justify-center">
              <Search className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-1">SEO Suite</p>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#111111]" data-testid="page-title">SEO Tools</h1>
              <p className="text-slate-600 mt-1.5">Optimize your website for search engines with professional SEO tools</p>
            </div>
          </div>
        </div>

        {/* AI SEO Toolkit promo banner — Premium */}
        <div
          onClick={() => navigate("/tools/seo/ai")}
          data-testid="ai-seo-promo-banner"
          className="mb-10 cursor-pointer p-6 rounded-3xl bg-[#111111] text-white hover:bg-[#000] transition-all hover:-translate-y-0.5"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#A855F7]"/>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <strong className="text-base font-semibold">AI SEO Toolkit</strong>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#A855F7] text-white">NEW</span>
                </div>
                <p className="text-sm text-slate-300 mt-0.5">Live Google SERP keywords · Claude AI content grader · meta tag generator · schema · 6 tools in one</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#111111] font-medium text-sm hover:bg-slate-100">
              Try free <ArrowRight className="w-4 h-4"/>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">SEO Tools</h3>
              <div className="space-y-2">
                {seoTools.map((tool) => (
                  tool.link ? (
                    <button
                      key={tool.id}
                      onClick={() => navigate(tool.link)}
                      data-testid={`tool-${tool.id}`}
                      className="w-full text-left px-4 py-3 rounded-lg transition-all bg-gradient-to-r from-violet-50 to-purple-50 hover:from-violet-100 hover:to-purple-100 text-violet-700 border border-violet-200"
                    >
                      <div className="flex items-center gap-3">
                        {tool.icon}
                        <div>
                          <span className="text-sm font-medium">{tool.name}</span>
                          <p className="text-xs text-violet-500">{tool.description}</p>
                        </div>
                      </div>
                    </button>
                  ) : tool.isLink ? (
                    <button
                      key={tool.id}
                      onClick={() => navigate(tool.linkTo)}
                      data-testid={`tool-${tool.id}`}
                      className="w-full text-left px-4 py-3 rounded-lg transition-all bg-gradient-to-r from-violet-50 to-purple-50 hover:from-violet-100 hover:to-purple-100 text-violet-700 border border-violet-200 relative"
                    >
                      <div className="flex items-center gap-3">
                        {tool.icon}
                        <div className="flex-1">
                          <span className="text-sm font-medium inline-flex items-center gap-2">
                            {tool.name}
                            {tool.badge && (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-purple-600 to-pink-500 text-white">{tool.badge}</span>
                            )}
                          </span>
                          <p className="text-xs text-violet-500">{tool.description}</p>
                        </div>
                      </div>
                    </button>
                  ) : (
                    <button
                      key={tool.id}
                      onClick={() => setActiveTool(tool.id)}
                      data-testid={`tool-${tool.id}`}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        activeTool === tool.id
                          ? 'bg-violet-100 text-violet-700 font-medium'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {tool.icon}
                        <span className="text-sm">{tool.name}</span>
                      </div>
                    </button>
                  )
                ))}
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Keyword Research Tool */}
            {activeTool === "keyword-research" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Target className="w-6 h-6 text-violet-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Advanced Keyword Research</h2>
                    <p className="text-gray-600">Find keywords with PAA, Related Searches, Long-Tail & SERP Analysis</p>
                  </div>
                </div>

                <div className="flex gap-4 mb-6">
                  <div className="flex-1 relative">
                    <Input
                      placeholder="Enter seed keyword (e.g., 'digital marketing', 'seo tools')"
                      value={keywordInput}
                      onChange={(e) => handleKeywordInputChange(e.target.value)}
                      onFocus={() => keywordInput.length >= 2 && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setShowSuggestions(false);
                          handleKeywordResearch();
                        }
                      }}
                      data-testid="keyword-input"
                    />
                    {/* Autocomplete Suggestions Dropdown */}
                    {showSuggestions && keywordSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {loadingSuggestions ? (
                          <div className="p-3 text-center text-gray-500 text-sm">
                            <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                            Loading suggestions...
                          </div>
                        ) : (
                          keywordSuggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              className="w-full text-left px-4 py-2 hover:bg-violet-50 text-gray-700 text-sm flex items-center gap-2 transition-colors"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectSuggestion(suggestion);
                              }}
                              data-testid={`keyword-suggestion-${idx}`}
                            >
                              <Search className="w-3 h-3 text-gray-400" />
                              {suggestion}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={handleKeywordResearch}
                    disabled={loadingKeywords || (usageStatus && usageStatus.remaining <= 0 && !usageStatus.allowed)}
                    className="bg-violet-700 hover:bg-violet-800"
                    data-testid="keyword-search-btn"
                  >
                    {loadingKeywords ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Researching...</>
                    ) : "Research Keyword"}
                  </Button>
                </div>

                {/* Usage Status Indicator */}
                {usageStatus && (
                  <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
                    usageStatus.remaining <= 0 
                      ? 'bg-red-50 border border-red-200' 
                      : usageStatus.remaining <= 1 
                        ? 'bg-yellow-50 border border-yellow-200'
                        : 'bg-gray-50 border border-gray-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      <Search className={`w-4 h-4 ${usageStatus.remaining <= 0 ? 'text-red-500' : 'text-gray-500'}`} />
                      <span className="text-sm text-gray-700">
                        <strong>{usageStatus.remaining}</strong> of <strong>{usageStatus.limit === -1 ? '∞' : usageStatus.limit}</strong> searches remaining
                        <span className="text-gray-500 ml-1">({usageStatus.reset_type} reset)</span>
                      </span>
                    </div>
                    {usageStatus.plan === 'free' && usageStatus.remaining > 0 && (
                      <span className="text-xs text-violet-600 font-medium">
                        Upgrade to Pro for 300 searches/month →
                      </span>
                    )}
                    {usageStatus.remaining <= 0 && (
                      <Button 
                        size="sm" 
                        className="bg-violet-600 hover:bg-violet-700 text-xs"
                        onClick={() => navigate('/pricing')}
                      >
                        Upgrade to Pro
                      </Button>
                    )}
                  </div>
                )}

                {advancedResults && (
                  <div className="space-y-6">
                    {/* Data Source Label */}
                    {advancedResults.data_source && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`px-2 py-1 rounded ${advancedResults.data_type === 'REAL' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {advancedResults.data_type || 'API'}: {advancedResults.data_source}
                        </span>
                        {advancedResults.disclaimer && <span className="text-gray-400">{advancedResults.disclaimer}</span>}
                      </div>
                    )}
                    {/* Export Buttons */}
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Results for: <span className="text-violet-700">"{keywordInput}"</span>
                      </h3>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => exportToCSV(
                            [
                              ...(advancedResults.keyword_planner || []).map(kp => ({
                                keyword: kp.keyword,
                                volume: kp.avg_monthly_searches,
                                competition: kp.competition,
                                cpc: kp.cpc,
                                intent: kp.intent || 'info',
                                type: 'planner'
                              })),
                              ...(advancedResults.long_tail_keywords || []).map(lt => ({
                                keyword: lt.keyword,
                                volume: lt.search_volume,
                                competition: lt.difficulty_label,
                                cpc: lt.cpc,
                                intent: lt.intent,
                                type: 'long-tail'
                              })),
                              ...(advancedResults.related_searches || []).map(rs => ({
                                keyword: rs.keyword,
                                volume: rs.search_volume,
                                competition: 'N/A',
                                cpc: 'N/A',
                                intent: 'N/A',
                                type: 'related'
                              }))
                            ],
                            `keyword_research_${keywordInput.replace(/\s+/g, '_')}`
                          )}
                          data-testid="keyword-export-csv-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Export CSV
                        </Button>
                        <Button 
                          onClick={() => exportKeywordsToPDF(advancedResults, keywordInput)}
                          className="bg-violet-700 hover:bg-violet-800"
                          size="sm"
                          data-testid="keyword-export-pdf-btn"
                        >
                          <FileDown className="w-4 h-4 mr-2" />
                          Export to PDF
                        </Button>
                      </div>
                    </div>

                    {/* Keyword Overview Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="p-4 bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl text-center border border-violet-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Search Volume</p>
                        <p className="text-2xl font-bold text-violet-700">{typeof advancedResults.keyword_metrics.search_volume === 'number' ? advancedResults.keyword_metrics.search_volume.toLocaleString() : 'N/A'}</p>
                        <p className="text-xs text-gray-500">/month</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl text-center border border-blue-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Difficulty</p>
                        <p className={`text-2xl font-bold ${advancedResults.keyword_metrics.difficulty < 30 ? 'text-green-600' : advancedResults.keyword_metrics.difficulty < 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {advancedResults.keyword_metrics.difficulty}
                        </p>
                        <p className="text-xs text-gray-500">{advancedResults.keyword_metrics.difficulty_label}</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl text-center border border-green-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">CPC</p>
                        <p className="text-2xl font-bold text-green-700">${advancedResults.keyword_metrics.cpc}</p>
                        <p className="text-xs text-gray-500">per click</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl text-center border border-orange-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Competition</p>
                        <p className="text-2xl font-bold text-orange-700">{Math.round(advancedResults.keyword_metrics.competition * 100)}%</p>
                        <p className="text-xs text-gray-500">{advancedResults.keyword_metrics.competition > 0.7 ? 'High' : advancedResults.keyword_metrics.competition > 0.4 ? 'Medium' : 'Low'}</p>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl text-center border border-pink-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Trend</p>
                        <p className="text-2xl font-bold text-pink-700 capitalize">{advancedResults.keyword_metrics.trend}</p>
                        <p className="text-xs text-gray-500">{advancedResults.keyword_metrics.seasonal ? 'Seasonal' : 'Evergreen'}</p>
                      </div>
                    </div>

                    {/* Tabs for different sections */}
                    <Tabs value={activeKeywordTab} onValueChange={setActiveKeywordTab} className="w-full">
                      <TabsList className="grid w-full grid-cols-7 mb-4">
                        <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
                        <TabsTrigger value="planner" className="text-xs">Keyword Planner</TabsTrigger>
                        <TabsTrigger value="suggestions" className="text-xs">Google Suggestions</TabsTrigger>
                        <TabsTrigger value="paa" className="text-xs">PAA</TabsTrigger>
                        <TabsTrigger value="related" className="text-xs">Related</TabsTrigger>
                        <TabsTrigger value="longtail" className="text-xs">Long-Tail</TabsTrigger>
                        <TabsTrigger value="serp" className="text-xs">SERP</TabsTrigger>
                      </TabsList>

                      {/* Overview Tab */}
                      <TabsContent value="overview" className="space-y-6">
                        {/* SERP Features */}
                        <div>
                          <h3 className="font-semibold mb-3 flex items-center gap-2">
                            <Search className="w-4 h-4" /> SERP Features Detected
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {(advancedResults.serp_features || []).filter(f => f && f.type).map((feature, idx) => (
                              <Badge key={idx} variant="outline" className="px-3 py-1.5 bg-blue-50 text-blue-700 border-blue-200">
                                {feature.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                {feature.count && <span className="ml-1 text-xs">({feature.count})</span>}
                              </Badge>
                            ))}
                            {(!advancedResults.serp_features || advancedResults.serp_features.length === 0) && (
                              <span className="text-xs text-gray-500">No SERP features detected for this keyword.</span>
                            )}
                          </div>
                        </div>

                        {/* Analysis Summary */}
                        <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-200">
                          <h3 className="font-semibold mb-3">Analysis Summary</h3>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${advancedResults.analysis_summary.ranking_opportunity === 'High' ? 'bg-green-500' : advancedResults.analysis_summary.ranking_opportunity === 'Medium' ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                              <span className="text-sm">Ranking Opportunity: <strong>{advancedResults.analysis_summary.ranking_opportunity}</strong></span>
                            </div>
                            <div className="flex items-center gap-2">
                              {advancedResults.analysis_summary.featured_snippet_opportunity ? (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-orange-500" />
                              )}
                              <span className="text-sm">Featured Snippet Opportunity</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-700">Recommendations:</p>
                            <ul className="list-disc list-inside space-y-1">
                              {advancedResults.analysis_summary.recommendations.map((rec, idx) => (
                                <li key={idx} className="text-sm text-gray-600">{rec}</li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Total Results */}
                        <div className="text-center text-sm text-gray-500">
                          Estimated {advancedResults.total_results_estimate} results in Google
                        </div>
                      </TabsContent>

                      {/* People Also Ask Tab */}
                      <TabsContent value="paa" className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg">People Also Ask Questions</h3>
                          <Badge className="bg-blue-100 text-blue-700">{advancedResults.people_also_ask.length} questions</Badge>
                        </div>
                        <div className="space-y-3">
                          {advancedResults.people_also_ask.map((paa, idx) => (
                            <Card key={idx} className="p-4 border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-sm font-bold text-blue-700">{paa.position}</span>
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 mb-2">{paa.question}</h4>
                                  <p className="text-sm text-gray-600 mb-2">{paa.answer_preview}</p>
                                  <div className="flex items-center gap-2 text-xs text-gray-400">
                                    <ExternalLink className="w-3 h-3" />
                                    <span>{paa.source}</span>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                          <p className="text-sm text-yellow-800">
                            <strong>Tip:</strong> Answer these questions in your content to increase chances of appearing in the PAA box!
                          </p>
                        </div>
                      </TabsContent>

                      {/* Related Searches Tab */}
                      <TabsContent value="related" className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-lg">Related Searches</h3>
                            <p className="text-sm text-gray-500">Keywords people also search for</p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => exportToCSV(
                              advancedResults.related_searches.map(rs => ({
                                keyword: rs.keyword,
                                search_volume: rs.search_volume,
                                trend: rs.trend,
                                source: rs.source || 'N/A'
                              })),
                              'related_searches'
                            )}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Export
                          </Button>
                        </div>
                        <div className="overflow-x-auto rounded-lg border">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-violet-50 to-purple-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-violet-800">#</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-violet-800">Keyword</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold text-violet-800">Search Volume</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold text-violet-800">Trend</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold text-violet-800">Relevance</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y bg-white">
                              {advancedResults.related_searches.map((rs, idx) => {
                                const maxVol = Math.max(...advancedResults.related_searches.map(r => r.search_volume || 0));
                                const relevance = maxVol > 0 ? Math.round((rs.search_volume / maxVol) * 100) : 50;
                                return (
                                  <tr key={idx} className="hover:bg-violet-50/50 transition-colors">
                                    <td className="px-4 py-3 text-gray-400 text-sm">{idx + 1}</td>
                                    <td className="px-4 py-3">
                                      <span className="text-sm font-medium text-gray-900">{rs.keyword}</span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <div className="flex items-center justify-center gap-2">
                                        <span className="text-sm font-semibold text-violet-700">{rs.search_volume?.toLocaleString() || 'N/A'}</span>
                                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                          <div 
                                            className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"
                                            style={{ width: `${relevance}%` }}
                                          />
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <Badge className={
                                        rs.trend === 'rising' ? 'bg-green-100 text-green-700 border-green-200' :
                                        rs.trend === 'declining' ? 'bg-red-100 text-red-700 border-red-200' :
                                        'bg-gray-100 text-gray-700 border-gray-200'
                                      }>
                                        {rs.trend === 'rising' ? '↑ Rising' : rs.trend === 'declining' ? '↓ Declining' : '→ Stable'}
                                      </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span className={`text-sm font-medium ${relevance > 70 ? 'text-green-600' : relevance > 40 ? 'text-yellow-600' : 'text-gray-500'}`}>
                                        {relevance}%
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </TabsContent>

                      {/* Long-Tail Keywords Tab */}
                      <TabsContent value="longtail" className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg">Long-Tail Keywords</h3>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => exportToCSV(
                              advancedResults.long_tail_keywords.map(lt => ({
                                keyword: lt.keyword,
                                search_volume: lt.search_volume,
                                difficulty: lt.difficulty,
                                difficulty_label: lt.difficulty_label,
                                cpc: lt.cpc,
                                intent: lt.intent,
                                word_count: lt.word_count,
                                opportunity_score: lt.opportunity_score
                              })),
                              'long_tail_keywords'
                            )}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Export
                          </Button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Keyword</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">Volume</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">Difficulty</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">CPC</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">Intent</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">Opportunity</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {advancedResults.long_tail_keywords.map((lt, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-3">
                                    <div>
                                      <span className="text-sm font-medium text-gray-900">{lt.keyword}</span>
                                      <p className="text-xs text-gray-500">{lt.word_count} words</p>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center text-sm">{typeof lt.search_volume === 'number' ? lt.search_volume.toLocaleString() : 'N/A'}</td>
                                  <td className="px-4 py-3 text-center">
                                    <Badge className={getDifficultyColor(lt.difficulty)}>
                                      {lt.difficulty} - {lt.difficulty_label}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3 text-center text-sm">${lt.cpc}</td>
                                  <td className="px-4 py-3 text-center">
                                    <Badge variant="outline" className="capitalize text-xs">{lt.intent}</Badge>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <div className={`w-2 h-2 rounded-full ${lt.opportunity_score > 50 ? 'bg-green-500' : lt.opportunity_score > 25 ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                                      <span className="text-sm font-semibold">{lt.opportunity_score}</span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-green-800">
                            <strong>Pro Tip:</strong> Long-tail keywords have lower competition and higher conversion rates. Target keywords with high opportunity scores first!
                          </p>
                        </div>
                      </TabsContent>

                      {/* SERP Results Tab */}
                      <TabsContent value="serp" className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg">Google Search Results Preview</h3>
                          <Badge className="bg-gray-100 text-gray-700">Top 10 Results</Badge>
                        </div>
                        <div className="space-y-4">
                          {advancedResults.serp_results.map((result, idx) => (
                            <Card 
                              key={idx} 
                              className={`p-4 hover:shadow-md transition-shadow ${
                                result.is_featured ? 'border-2 border-blue-400 bg-blue-50' : ''
                              } ${result.type === 'video' ? 'border-l-4 border-l-red-500' : ''}`}
                            >
                              <div className="flex items-start gap-4">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  result.position <= 3 ? 'bg-green-100 text-green-700' :
                                  result.position <= 7 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  <span className="text-sm font-bold">#{result.position}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  {result.is_featured && (
                                    <Badge className="bg-blue-500 text-white text-xs mb-2">Featured Snippet ({result.snippet_type})</Badge>
                                  )}
                                  {result.type === 'video' && (
                                    <Badge className="bg-red-500 text-white text-xs mb-2">Video - {result.video_duration} • {result.video_views}</Badge>
                                  )}
                                  <a href={result.url} target="_blank" rel="noopener noreferrer" className="text-sm text-green-700 hover:underline block truncate">
                                    {result.url}
                                  </a>
                                  <h4 className="font-semibold text-blue-700 hover:underline cursor-pointer mt-1 text-lg">
                                    {result.title}
                                  </h4>
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{result.snippet || result.description}</p>
                                  <div className="flex items-center gap-2 mt-2">
                                    <Badge variant="outline" className="text-xs">{result.domain}</Badge>
                                    {result.type && (
                                      <Badge variant="outline" className="text-xs capitalize">{result.type.replace('_', ' ')}</Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                        <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                          <p className="text-sm text-violet-800">
                            <strong>Analysis:</strong> Review these top-ranking pages to understand what content ranks well. Look for content gaps and opportunities to create better content!
                          </p>
                        </div>
                      </TabsContent>

                      {/* Keyword Planner Tab */}
                      <TabsContent value="planner" className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-blue-600" />
                            Google Keyword Planner Data
                          </h3>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => exportToCSV(
                              advancedResults.keyword_planner?.map(kp => ({
                                keyword: kp.keyword,
                                avg_monthly_searches: kp.avg_monthly_searches,
                                competition: kp.competition,
                                competition_index: kp.competition_index,
                                cpc: kp.cpc,
                                top_of_page_bid_low: kp.top_of_page_bid_low,
                                top_of_page_bid_high: kp.top_of_page_bid_high,
                                yoy_change: kp.yoy_change,
                                three_month_change: kp.three_month_change
                              })) || [],
                              'keyword_planner_data'
                            )}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Export CSV
                          </Button>
                        </div>

                        {/* Summary Stats */}
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                            <p className="text-xs text-gray-500 uppercase">Total Keywords</p>
                            <p className="text-2xl font-bold text-blue-700">{advancedResults.keyword_planner?.length || 0}</p>
                          </div>
                          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                            <p className="text-xs text-gray-500 uppercase">Avg. CPC</p>
                            <p className="text-2xl font-bold text-green-700">
                              ${(advancedResults.keyword_planner?.reduce((acc, kp) => acc + kp.cpc, 0) / (advancedResults.keyword_planner?.length || 1)).toFixed(2)}
                            </p>
                          </div>
                          <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-200">
                            <p className="text-xs text-gray-500 uppercase">Total Volume</p>
                            <p className="text-2xl font-bold text-purple-700">
                              {advancedResults.keyword_planner?.reduce((acc, kp) => acc + kp.avg_monthly_searches, 0).toLocaleString()}
                            </p>
                          </div>
                        </div>

                        {/* Keyword Planner Table */}
                        <div className="overflow-x-auto border rounded-xl">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                              <tr>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-blue-800">#</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800">Keyword</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-blue-800">Volume</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-blue-800">Competition</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-blue-800">CPC</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-blue-800">Intent</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-blue-800">Type</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-blue-800">Trend</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y bg-white">
                              {advancedResults.keyword_planner?.map((kp, idx) => (
                                <tr key={idx} className={`hover:bg-blue-50/50 transition-colors ${idx === 0 ? 'bg-blue-50' : ''}`}>
                                  <td className="px-3 py-3 text-gray-400 text-sm">{idx + 1}</td>
                                  <td className="px-4 py-3">
                                    <div>
                                      <span className={`text-sm font-medium ${idx === 0 ? 'text-blue-700' : 'text-gray-900'}`}>
                                        {kp.keyword}
                                      </span>
                                      {idx === 0 && <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">Seed</Badge>}
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <div className="flex flex-col items-center">
                                      <span className="text-sm font-bold text-blue-700">{kp.avg_monthly_searches?.toLocaleString()}</span>
                                      <div className="w-16 h-1.5 bg-gray-200 rounded-full mt-1">
                                        <div 
                                          className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                                          style={{ width: `${Math.min(100, (kp.avg_monthly_searches / (advancedResults.keyword_planner?.[0]?.avg_monthly_searches || 1)) * 100)}%` }}
                                        />
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <Badge className={
                                      kp.competition === 'High' ? 'bg-red-100 text-red-700 border-red-200' :
                                      kp.competition === 'Medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                                      'bg-green-100 text-green-700 border-green-200'
                                    }>
                                      {kp.competition}
                                    </Badge>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <span className="text-sm font-medium text-green-700">${kp.cpc}</span>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <Badge variant="outline" className={
                                      kp.intent === 'transactional' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                      kp.intent === 'commercial' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                      kp.intent === 'local' ? 'bg-teal-50 text-teal-700 border-teal-200' :
                                      kp.intent === 'navigational' ? 'bg-pink-50 text-pink-700 border-pink-200' :
                                      'bg-blue-50 text-blue-700 border-blue-200'
                                    }>
                                      {kp.intent || 'info'}
                                    </Badge>
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    {kp.seasonal ? (
                                      <Badge className="bg-amber-100 text-amber-700 text-xs">Seasonal</Badge>
                                    ) : (
                                      <span className="text-xs text-gray-500">Evergreen</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-3 text-center">
                                    <Badge className={
                                      kp.yoy_change > 0 ? 'bg-green-100 text-green-700' :
                                      kp.yoy_change < 0 ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-700'
                                    }>
                                      {kp.yoy_change > 0 ? '↑' : kp.yoy_change < 0 ? '↓' : '→'} {Math.abs(kp.yoy_change || 0)}%
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Intent Legend */}
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium text-gray-700 mb-2">Intent Types:</p>
                          <div className="flex flex-wrap gap-3 text-xs">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Informational</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"></span> Commercial</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500"></span> Transactional</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-500"></span> Local</span>
                          </div>
                        </div>

                        {/* Trending Keywords Section */}
                        {advancedResults.trending_keywords && advancedResults.trending_keywords.length > 0 && (
                          <div className="mt-6">
                            <h4 className="font-semibold mb-3 flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-orange-500" />
                              Trending Keywords
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {advancedResults.trending_keywords.map((tk, idx) => (
                                <div key={idx} className={`p-3 rounded-lg border ${tk.is_breakout ? 'bg-orange-50 border-orange-300' : 'bg-gray-50 border-gray-200'}`}>
                                  <p className="text-sm font-medium text-gray-900 truncate">{tk.keyword}</p>
                                  <div className="flex items-center justify-between mt-2">
                                    <span className="text-xs text-gray-500">{typeof tk.volume === 'number' ? tk.volume.toLocaleString() : 'N/A'} vol</span>
                                    <Badge className={tk.is_breakout ? 'bg-orange-500 text-white' : 'bg-green-100 text-green-700'}>
                                      {tk.is_breakout ? '🔥 Breakout' : `+${tk.growth_percent}%`}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm text-blue-800">
                            <strong>💡 Pro Tip:</strong> Keywords with low competition and high search volume offer the best opportunity. Focus on keywords with CPC under $3 and competition index below 50 for easier ranking.
                          </p>
                        </div>
                      </TabsContent>

                      {/* Google Suggestions Tab */}
                      <TabsContent value="suggestions" className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            <Search className="w-5 h-5 text-purple-600" />
                            Google Autocomplete Suggestions
                          </h3>
                          <Badge className="bg-purple-100 text-purple-700">
                            {advancedResults.google_suggestions?.total_suggestions || 0} suggestions
                          </Badge>
                        </div>

                        {/* Suggestions Categories */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Question-Based Suggestions */}
                          <Card className="p-4 border-2 border-blue-200">
                            <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-700">
                              <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-sm">?</span>
                              Question Keywords
                            </h4>
                            <div className="space-y-2">
                              {advancedResults.google_suggestions?.questions?.map((sug, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                                  <span className="text-sm text-gray-800">{sug.suggestion}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">{sug.estimated_volume} vol</span>
                                    <Badge variant="outline" className="text-xs">${sug.cpc}</Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card>

                          {/* Comparison Suggestions */}
                          <Card className="p-4 border-2 border-orange-200">
                            <h4 className="font-semibold mb-3 flex items-center gap-2 text-orange-700">
                              <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-sm">⚖</span>
                              Comparison Keywords
                            </h4>
                            <div className="space-y-2">
                              {advancedResults.google_suggestions?.comparisons?.map((sug, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                                  <span className="text-sm text-gray-800">{sug.suggestion}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">{sug.estimated_volume} vol</span>
                                    <Badge variant="outline" className="text-xs">${sug.cpc}</Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card>

                          {/* Preposition Suggestions */}
                          <Card className="p-4 border-2 border-green-200">
                            <h4 className="font-semibold mb-3 flex items-center gap-2 text-green-700">
                              <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-sm">🔗</span>
                              Preposition Keywords
                            </h4>
                            <div className="space-y-2">
                              {advancedResults.google_suggestions?.prepositions?.map((sug, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                                  <span className="text-sm text-gray-800">{sug.suggestion}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">{sug.estimated_volume} vol</span>
                                    <Badge variant="outline" className="text-xs">${sug.cpc}</Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card>

                          {/* Alphabetical Suggestions */}
                          <Card className="p-4 border-2 border-purple-200">
                            <h4 className="font-semibold mb-3 flex items-center gap-2 text-purple-700">
                              <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-sm">🔤</span>
                              A-Z Suggestions
                            </h4>
                            <div className="max-h-64 overflow-y-auto space-y-1">
                              {advancedResults.google_suggestions?.alphabetical?.slice(0, 20).map((sug, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-purple-50 rounded hover:bg-purple-100 transition-colors">
                                  <div className="flex items-center gap-2">
                                    <Badge className="bg-purple-200 text-purple-700 text-xs w-5 h-5 flex items-center justify-center rounded-full">
                                      {sug.letter?.toUpperCase()}
                                    </Badge>
                                    <span className="text-sm text-gray-800">{sug.suggestion}</span>
                                  </div>
                                  <span className="text-xs text-gray-500">{sug.estimated_volume} vol</span>
                                </div>
                              ))}
                            </div>
                            {advancedResults.google_suggestions?.alphabetical?.length > 20 && (
                              <p className="text-xs text-gray-500 text-center mt-2">
                                + {advancedResults.google_suggestions.alphabetical.length - 20} more suggestions
                              </p>
                            )}
                          </Card>
                        </div>

                        {/* Export All Suggestions */}
                        <div className="flex justify-center mt-4">
                          <Button 
                            variant="outline"
                            onClick={() => {
                              const allSuggestions = [
                                ...(advancedResults.google_suggestions?.questions || []),
                                ...(advancedResults.google_suggestions?.comparisons || []),
                                ...(advancedResults.google_suggestions?.prepositions || []),
                                ...(advancedResults.google_suggestions?.alphabetical || [])
                              ].map(s => ({
                                suggestion: s.suggestion,
                                type: s.type || 'alphabetical',
                                estimated_volume: s.estimated_volume,
                                cpc: s.cpc
                              }));
                              exportToCSV(allSuggestions, 'google_suggestions');
                            }}
                            className="gap-2"
                          >
                            <Download className="w-4 h-4" />
                            Export All Suggestions
                          </Button>
                        </div>

                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <p className="text-sm text-purple-800">
                            <strong>💡 Content Idea:</strong> Question keywords are perfect for FAQ sections and blog posts. Comparison keywords work great for review articles. Use these suggestions to create comprehensive content that matches user search intent!
                          </p>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                )}
              </Card>
            )}

            {/* Backlink Analyzer */}
            {activeTool === "backlink-analyzer" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Link2 className="w-6 h-6 text-blue-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Backlink Analyzer</h2>
                    <p className="text-gray-600">Analyze backlink profile and domain authority</p>
                  </div>
                </div>

                <div className="flex gap-4 mb-6">
                  <Input
                    placeholder="Enter URL to analyze (e.g., example.com)"
                    value={backlinkUrl}
                    onChange={(e) => setBacklinkUrl(e.target.value)}
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && handleBacklinkAnalysis()}
                    data-testid="backlink-url-input"
                  />
                  <Button 
                    onClick={handleBacklinkAnalysis}
                    disabled={loadingBacklinks}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="analyze-backlinks-btn"
                  >
                    {loadingBacklinks ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                    ) : "Analyze"}
                  </Button>
                </div>

                {backlinkResults && (
                  <div className="space-y-6">
                    {/* Profile Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Total Backlinks</p>
                        <p className="text-3xl font-bold text-blue-600">{backlinkResults.total_backlinks || 0}</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Unique Domains</p>
                        <p className="text-3xl font-bold text-green-600">{backlinkResults.referring_domains || 0}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Follow Links</p>
                        <p className="text-3xl font-bold text-purple-600">{backlinkResults.dofollow_links || 0}</p>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Avg. Domain Authority</p>
                        <p className="text-3xl font-bold text-orange-600">{backlinkResults.average_domain_authority || 0}</p>
                      </div>
                    </div>

                    {/* Top Domains */}
                    {backlinkResults.top_referring_domains && backlinkResults.top_referring_domains.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">Top Linking Domains</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Domain</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">Links</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">Authority</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {backlinkResults.top_referring_domains.map((domain, idx) => (
                                <tr key={idx}>
                                  <td className="px-4 py-3 text-sm flex items-center gap-2">
                                    <ExternalLink className="w-4 h-4 text-gray-400" />
                                    {domain.domain}
                                  </td>
                                  <td className="px-4 py-3 text-center">{domain.backlinks || 0}</td>
                                  <td className="px-4 py-3 text-center">
                                    <Badge className={domain.authority >= 70 ? 'bg-green-100 text-green-800' : domain.authority >= 40 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                                      DA: {domain.authority}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    {backlinkResults.recommendations && (
                      <div>
                        <h3 className="font-semibold mb-3">Recommendations</h3>
                        <div className="space-y-2">
                          {backlinkResults.recommendations.map((rec, idx) => (
                            <div key={idx} className="p-3 bg-blue-50 rounded border-l-4 border-blue-400">
                              <p className="font-medium text-sm">{rec.message}</p>
                              <p className="text-sm text-gray-600 mt-1">{rec.recommendation}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Rank Tracking */}
            {activeTool === "rank-tracking" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Rank Tracking</h2>
                    <p className="text-gray-600">Track keyword rankings over time</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <Input
                    placeholder="Enter domain (e.g., example.com)"
                    value={trackingDomain}
                    onChange={(e) => setTrackingDomain(e.target.value)}
                    data-testid="tracking-domain-input"
                  />
                  <textarea
                    className="w-full min-h-[150px] p-3 border rounded-md"
                    placeholder="Enter keywords to track (one per line)&#10;seo tools&#10;website optimization&#10;keyword research"
                    value={trackingKeywords}
                    onChange={(e) => setTrackingKeywords(e.target.value)}
                    data-testid="tracking-keywords-input"
                  />
                  <Button 
                    onClick={handleRankTracking}
                    disabled={loadingRanking}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="track-rankings-btn"
                  >
                    {loadingRanking ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Tracking...</>
                    ) : "Track Rankings"}
                  </Button>
                </div>

                {rankingResults && (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Keywords Tracked</p>
                        <p className="text-3xl font-bold text-blue-600">{rankingResults.total_keywords}</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Avg. Position</p>
                        <p className="text-3xl font-bold text-green-600">#{rankingResults.average_position}</p>
                      </div>
                    </div>

                    {/* Rankings Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold">Keyword</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold">Position</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold">Search Volume</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {rankingResults.rankings.map((ranking, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">{ranking.keyword}</td>
                              <td className="px-4 py-3 text-center">
                                <Badge className={
                                  ranking.position <= 10 ? 'bg-green-100 text-green-800' :
                                  ranking.position <= 30 ? 'bg-blue-100 text-blue-800' :
                                  'bg-orange-100 text-orange-800'
                                }>
                                  #{ranking.position}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-center text-sm">
                                {typeof ranking.search_volume === 'number' ? ranking.search_volume.toLocaleString() : 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <Badge variant="outline">New</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Site Audit */}
            {activeTool === "site-audit" && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-orange-100 rounded-xl flex items-center justify-center">
                      <Search className="w-6 h-6 text-red-700" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Advanced Site Audit</h2>
                      <p className="text-gray-600">Complete SEO, Performance, Security & Mobile Analysis</p>
                    </div>
                  </div>
                  {auditResults && (
                    <Button 
                      variant="outline"
                      onClick={() => exportToPDF(auditResults)}
                      className="gap-2"
                      data-testid="download-pdf-btn"
                    >
                      <FileDown className="w-4 h-4" />
                      Download PDF
                    </Button>
                  )}
                </div>

                <div className="flex gap-4 mb-6">
                  <Input
                    placeholder="Enter URL to audit (e.g., example.com)"
                    value={auditUrl}
                    onChange={(e) => setAuditUrl(e.target.value)}
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && handleSiteAudit()}
                    data-testid="audit-url-input"
                  />
                  <Button 
                    onClick={handleSiteAudit}
                    disabled={loadingAudit}
                    className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
                    data-testid="run-audit-btn"
                  >
                    {loadingAudit ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Auditing...</>
                    ) : "Run Full Audit"}
                  </Button>
                </div>

                {auditResults && (
                  <div className="space-y-6">
                    {/* Score Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      <div className={`p-4 rounded-xl text-center border-2 ${auditResults.overall_score >= 70 ? 'bg-green-50 border-green-200' : auditResults.overall_score >= 50 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                        <p className="text-xs text-gray-600 uppercase tracking-wide">Overall</p>
                        <p className={`text-3xl font-bold ${getScoreColor(auditResults.overall_score)}`}>
                          {auditResults.overall_score}
                        </p>
                      </div>
                      <div className="p-4 bg-violet-50 rounded-xl text-center border-2 border-violet-200">
                        <p className="text-xs text-gray-600 uppercase tracking-wide">SEO</p>
                        <p className={`text-3xl font-bold ${getScoreColor(auditResults.seo_score)}`}>
                          {auditResults.seo_score}
                        </p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-xl text-center border-2 border-blue-200">
                        <p className="text-xs text-gray-600 uppercase tracking-wide">Technical</p>
                        <p className={`text-3xl font-bold ${getScoreColor(auditResults.technical_score)}`}>
                          {auditResults.technical_score}
                        </p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-xl text-center border-2 border-green-200">
                        <p className="text-xs text-gray-600 uppercase tracking-wide">Performance</p>
                        <p className={`text-3xl font-bold ${getScoreColor(auditResults.performance_score)}`}>
                          {auditResults.performance_score}
                        </p>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-xl text-center border-2 border-orange-200">
                        <p className="text-xs text-gray-600 uppercase tracking-wide">Security</p>
                        <p className={`text-3xl font-bold ${getScoreColor(auditResults.security_score)}`}>
                          {auditResults.security_score}
                        </p>
                      </div>
                      <div className="p-4 bg-pink-50 rounded-xl text-center border-2 border-pink-200">
                        <p className="text-xs text-gray-600 uppercase tracking-wide">Mobile</p>
                        <p className={`text-3xl font-bold ${getScoreColor(auditResults.mobile_score)}`}>
                          {auditResults.mobile_score}
                        </p>
                      </div>
                    </div>

                    {/* Issues Summary */}
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">Issues Summary</h3>
                        <span className="text-sm text-gray-600">{auditResults.issues_summary?.total || 0} total issues</span>
                      </div>
                      <div className="flex gap-4 mt-3">
                        <div className="flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-600" />
                          <span className="text-sm">Critical: <strong>{auditResults.issues_summary?.critical || 0}</strong></span>
                        </div>
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-orange-500" />
                          <span className="text-sm">High: <strong>{auditResults.issues_summary?.high || 0}</strong></span>
                        </div>
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-500" />
                          <span className="text-sm">Medium: <strong>{auditResults.issues_summary?.medium || 0}</strong></span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span className="text-sm">Low: <strong>{auditResults.issues_summary?.low || 0}</strong></span>
                        </div>
                      </div>
                    </div>

                    {/* Tabs */}
                    <Tabs value={activeAuditTab} onValueChange={setActiveAuditTab} className="w-full">
                      <TabsList className="grid w-full grid-cols-6 mb-4">
                        <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
                        <TabsTrigger value="seo" className="text-xs">On-Page SEO</TabsTrigger>
                        <TabsTrigger value="performance" className="text-xs">Performance</TabsTrigger>
                        <TabsTrigger value="technical" className="text-xs">Technical</TabsTrigger>
                        <TabsTrigger value="security" className="text-xs">Security</TabsTrigger>
                        <TabsTrigger value="issues" className="text-xs">All Issues</TabsTrigger>
                      </TabsList>

                      {/* Overview Tab */}
                      <TabsContent value="overview" className="space-y-6">
                        {/* Core Web Vitals */}
                        <div>
                          <h3 className="font-semibold mb-3 flex items-center gap-2">
                            <Zap className="w-4 h-4 text-yellow-500" /> Core Web Vitals
                          </h3>
                          <div className="grid grid-cols-5 gap-3">
                            {auditResults.performance?.core_web_vitals && Object.entries(auditResults.performance.core_web_vitals).map(([key, cwv]) => (
                              <div key={key} className={`p-3 rounded-lg border-2 ${cwv.status === 'good' ? 'bg-green-50 border-green-200' : cwv.status === 'warning' ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                                <p className="text-xs text-gray-600 uppercase">{key.toUpperCase()}</p>
                                <p className="text-xl font-bold">{cwv.value}{cwv.unit}</p>
                                <Badge className={`text-xs mt-1 ${cwv.status === 'good' ? 'bg-green-100 text-green-700' : cwv.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                  {cwv.status.toUpperCase()}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-4 gap-4">
                          <div className="p-4 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-2 text-blue-700 mb-2">
                              <FileText className="w-4 h-4" />
                              <span className="text-sm font-medium">Content</span>
                            </div>
                            <p className="text-2xl font-bold">{auditResults.content?.word_count || 0}</p>
                            <p className="text-xs text-gray-600">words</p>
                          </div>
                          <div className="p-4 bg-green-50 rounded-lg">
                            <div className="flex items-center gap-2 text-green-700 mb-2">
                              <Link className="w-4 h-4" />
                              <span className="text-sm font-medium">Internal Links</span>
                            </div>
                            <p className="text-2xl font-bold">{auditResults.links?.internal || 0}</p>
                          </div>
                          <div className="p-4 bg-orange-50 rounded-lg">
                            <div className="flex items-center gap-2 text-orange-700 mb-2">
                              <ExternalLink className="w-4 h-4" />
                              <span className="text-sm font-medium">External Links</span>
                            </div>
                            <p className="text-2xl font-bold">{auditResults.links?.external || 0}</p>
                          </div>
                          <div className="p-4 bg-purple-50 rounded-lg">
                            <div className="flex items-center gap-2 text-purple-700 mb-2">
                              <Image className="w-4 h-4" />
                              <span className="text-sm font-medium">Images</span>
                            </div>
                            <p className="text-2xl font-bold">{auditResults.on_page_seo?.images?.total || 0}</p>
                          </div>
                        </div>

                        {/* Recommendations */}
                        {auditResults.recommendations && auditResults.recommendations.length > 0 && (
                          <div>
                            <h3 className="font-semibold mb-3">Top Recommendations</h3>
                            <div className="space-y-3">
                              {auditResults.recommendations.map((rec, idx) => (
                                <div key={idx} className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-200">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <Badge className={`text-xs mb-2 ${rec.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        {rec.priority.toUpperCase()} PRIORITY
                                      </Badge>
                                      <h4 className="font-semibold text-gray-900">{rec.title}</h4>
                                      <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                                    </div>
                                    <Badge className="bg-green-100 text-green-700 text-xs">{rec.estimated_impact}</Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </TabsContent>

                      {/* On-Page SEO Tab */}
                      <TabsContent value="seo" className="space-y-4">
                        <div className="grid gap-4">
                          {auditResults.on_page_seo && Object.entries(auditResults.on_page_seo).map(([key, data]) => (
                            <div key={key} className={`p-4 rounded-lg border-l-4 ${data.status === 'good' ? 'bg-green-50 border-green-500' : data.status === 'warning' ? 'bg-yellow-50 border-yellow-500' : 'bg-red-50 border-red-500'}`}>
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold capitalize text-gray-900">{key.replace('_', ' ')}</h4>
                                  {data.value !== undefined && (
                                    <p className="text-sm text-gray-700 mt-1 truncate max-w-md">{data.value || 'Not set'}</p>
                                  )}
                                  {data.length !== undefined && (
                                    <p className="text-xs text-gray-500">{data.length} characters</p>
                                  )}
                                  {data.count !== undefined && (
                                    <p className="text-sm text-gray-700">{data.count} found</p>
                                  )}
                                  <p className="text-xs text-gray-500 mt-1">{data.recommendation}</p>
                                </div>
                                <Badge className={`${data.status === 'good' ? 'bg-green-100 text-green-700' : data.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                  {data.status === 'good' ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                                  {data.status.toUpperCase()}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>

                      {/* Performance Tab */}
                      <TabsContent value="performance" className="space-y-6">
                        {/* Core Web Vitals Detail */}
                        <div>
                          <h3 className="font-semibold mb-4">Core Web Vitals</h3>
                          <div className="space-y-4">
                            {auditResults.performance?.core_web_vitals && Object.entries(auditResults.performance.core_web_vitals).map(([key, cwv]) => (
                              <div key={key} className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <h4 className="font-medium">{cwv.label}</h4>
                                    <p className="text-2xl font-bold">{cwv.value}{cwv.unit}</p>
                                  </div>
                                  <Badge className={`${cwv.status === 'good' ? 'bg-green-100 text-green-700' : cwv.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                    {cwv.status.toUpperCase()}
                                  </Badge>
                                </div>
                                <Progress value={cwv.status === 'good' ? 90 : cwv.status === 'warning' ? 60 : 30} className="h-2" />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Page Metrics */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-4 bg-blue-50 rounded-lg text-center">
                            <Clock className="w-5 h-5 mx-auto text-blue-600 mb-2" />
                            <p className="text-xs text-gray-600">Load Time</p>
                            <p className="text-xl font-bold">{auditResults.performance?.load_time?.toFixed(2) || 0}s</p>
                          </div>
                          <div className="p-4 bg-green-50 rounded-lg text-center">
                            <Activity className="w-5 h-5 mx-auto text-green-600 mb-2" />
                            <p className="text-xs text-gray-600">Page Size</p>
                            <p className="text-xl font-bold">{auditResults.performance?.page_size_kb?.toFixed(0) || 0} KB</p>
                          </div>
                          <div className="p-4 bg-purple-50 rounded-lg text-center">
                            <Eye className="w-5 h-5 mx-auto text-purple-600 mb-2" />
                            <p className="text-xs text-gray-600">Status Code</p>
                            <p className="text-xl font-bold">{auditResults.performance?.status_code || 0}</p>
                          </div>
                        </div>
                      </TabsContent>

                      {/* Technical Tab */}
                      <TabsContent value="technical" className="space-y-4">
                        <div className="grid gap-4">
                          {auditResults.technical_seo && Object.entries(auditResults.technical_seo).map(([key, data]) => (
                            <div key={key} className={`p-4 rounded-lg border ${data.status === 'good' ? 'bg-green-50 border-green-200' : data.status === 'warning' ? 'bg-yellow-50 border-yellow-200' : data.status === 'error' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {data.present ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <XCircle className="w-5 h-5 text-red-500" />
                                  )}
                                  <div>
                                    <h4 className="font-medium capitalize">{key.replace('_', ' ')}</h4>
                                    {data.value && <p className="text-xs text-gray-500">{data.value}</p>}
                                    <p className="text-xs text-gray-500">{data.recommendation}</p>
                                  </div>
                                </div>
                                <Badge className={`${data.status === 'good' ? 'bg-green-100 text-green-700' : data.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : data.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                                  {data.present ? 'PRESENT' : 'MISSING'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>

                      {/* Security Tab */}
                      <TabsContent value="security" className="space-y-4">
                        <div className="grid gap-4">
                          {auditResults.security && Object.entries(auditResults.security).map(([key, data]) => (
                            <div key={key} className={`p-4 rounded-lg border ${data.status === 'good' ? 'bg-green-50 border-green-200' : data.status === 'warning' ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {data.present || data.status === 'good' ? (
                                    <Lock className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <Unlock className="w-5 h-5 text-red-500" />
                                  )}
                                  <div>
                                    <h4 className="font-medium capitalize">{key.replace('_', ' ')}</h4>
                                    <p className="text-xs text-gray-500">{data.recommendation}</p>
                                  </div>
                                </div>
                                <Badge className={`${data.status === 'good' ? 'bg-green-100 text-green-700' : data.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                  {data.status.toUpperCase()}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Mobile Friendliness */}
                        <div className="mt-6">
                          <h3 className="font-semibold mb-3 flex items-center gap-2">
                            <Smartphone className="w-4 h-4" /> Mobile Friendliness
                          </h3>
                          <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium">Mobile Score</span>
                              <span className="text-2xl font-bold">{auditResults.mobile?.score || 0}</span>
                            </div>
                            <Progress value={auditResults.mobile?.score || 0} className="h-2 mb-4" />
                            <div className="grid grid-cols-2 gap-3">
                              <div className={`p-2 rounded ${auditResults.mobile?.viewport ? 'bg-green-100' : 'bg-red-100'}`}>
                                <p className="text-xs">Viewport: {auditResults.mobile?.viewport ? '✓' : '✗'}</p>
                              </div>
                              <div className={`p-2 rounded ${auditResults.mobile?.tap_targets?.status === 'good' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                                <p className="text-xs">Tap Targets: {auditResults.mobile?.tap_targets?.status}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      {/* All Issues Tab */}
                      <TabsContent value="issues" className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold">All Issues ({auditResults.issues?.length || 0})</h3>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => exportToCSV(
                              auditResults.issues?.map(i => ({
                                severity: i.severity,
                                category: i.category,
                                issue: i.issue,
                                recommendation: i.recommendation,
                                impact: i.impact || ''
                              })) || [],
                              'site_audit_issues'
                            )}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Export Issues
                          </Button>
                        </div>
                        <div className="space-y-3">
                          {auditResults.issues?.map((issue, idx) => (
                            <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                              issue.severity === 'critical' ? 'bg-red-50 border-red-600' :
                              issue.severity === 'high' ? 'bg-orange-50 border-orange-500' :
                              issue.severity === 'medium' ? 'bg-yellow-50 border-yellow-500' :
                              'bg-green-50 border-green-500'
                            }`}>
                              <div className="flex items-start gap-3">
                                {issue.severity === 'critical' || issue.severity === 'high' ? (
                                  <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                ) : (
                                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge className={`text-xs ${
                                      issue.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                      issue.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                                      issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-green-100 text-green-700'
                                    }`}>
                                      {issue.severity.toUpperCase()}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs">{issue.category}</Badge>
                                  </div>
                                  <h4 className="font-medium text-gray-900">{issue.issue}</h4>
                                  <p className="text-sm text-gray-600 mt-1">{issue.recommendation}</p>
                                  {issue.impact && (
                                    <p className="text-xs text-gray-500 mt-2 italic">Impact: {issue.impact}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                    </Tabs>

                    {/* Keywords Found */}
                    {auditResults.keywords && auditResults.keywords.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">Keywords Found on Page</h3>
                        <div className="flex flex-wrap gap-2">
                          {auditResults.keywords.map((kw, idx) => (
                            <Badge key={idx} variant="outline" className="bg-violet-50">{kw}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Site Structure */}
            {activeTool === "site-structure" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-6 h-6 text-purple-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Site Structure</h2>
                    <p className="text-gray-600">Analyze internal linking structure</p>
                  </div>
                </div>

                <div className="flex gap-4 mb-6">
                  <Input
                    placeholder="Enter URL to analyze (e.g., example.com)"
                    value={structureUrl}
                    onChange={(e) => setStructureUrl(e.target.value)}
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && handleStructureAnalysis()}
                    data-testid="structure-url-input"
                  />
                  <Button 
                    onClick={handleStructureAnalysis}
                    disabled={loadingStructure}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="analyze-structure-btn"
                  >
                    {loadingStructure ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                    ) : "Analyze"}
                  </Button>
                </div>

                {structureResults && (
                  <div className="space-y-6">
                    {/* Score */}
                    <div className="p-6 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600 mb-2">Structure Score</p>
                      <p className={`text-5xl font-bold ${getScoreColor(structureResults.structure_score)}`}>
                        {structureResults.structure_score}
                      </p>
                    </div>

                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-blue-50 rounded text-center">
                        <p className="text-sm text-gray-600">Internal Links</p>
                        <p className="text-2xl font-bold">{structureResults.summary.total_internal_links}</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded text-center">
                        <p className="text-sm text-gray-600">External Links</p>
                        <p className="text-2xl font-bold">{structureResults.summary.total_external_links}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded text-center">
                        <p className="text-sm text-gray-600">Unique Pages</p>
                        <p className="text-2xl font-bold">{structureResults.summary.unique_pages_found}</p>
                      </div>
                    </div>

                    {/* Depth Analysis */}
                    <div>
                      <h3 className="font-semibold mb-3">Link Depth Distribution</h3>
                      <div className="space-y-2">
                        {Object.entries(structureResults.depth_analysis).map(([level, count]) => (
                          <div key={level} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                            <span className="text-sm capitalize">{level.replace('_', ' ')}</span>
                            <Badge variant="secondary">{count} pages</Badge>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top Pages */}
                    {structureResults.top_pages && structureResults.top_pages.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">Most Linked Internal Pages</h3>
                        <div className="space-y-2">
                          {structureResults.top_pages.map((page, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                              <span className="text-sm truncate flex-1">{page.url}</span>
                              <Badge className="ml-2">{page.incoming_links} links</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Issues */}
                    {structureResults.issues && structureResults.issues.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">Issues Found</h3>
                        <div className="space-y-2">
                          {structureResults.issues.map((issue, idx) => (
                            <div key={idx} className="p-3 bg-orange-50 rounded border-l-4 border-orange-400">
                              <p className="font-medium text-sm">{issue.issue}</p>
                              <p className="text-sm text-gray-600 mt-1">{issue.description}</p>
                              <p className="text-sm text-gray-700 mt-1">→ {issue.recommendation}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Competitor Analysis - ENHANCED */}
            {activeTool === "competitor" && (
              <div className="space-y-6">
                {/* Header Card with Input */}
                <Card className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Competitor Analysis</h2>
                      <p className="text-gray-600">SEMrush-style competitive intelligence</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Your Domain</label>
                      <Input
                        placeholder="yourdomain.com"
                        value={domain1}
                        onChange={(e) => setDomain1(e.target.value)}
                        data-testid="domain1-input"
                        className="border-orange-200 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Competitor 1</label>
                      <Input
                        placeholder="competitor1.com"
                        value={domain2}
                        onChange={(e) => setDomain2(e.target.value)}
                        data-testid="domain2-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Competitor 2 (optional)</label>
                      <Input
                        placeholder="competitor2.com"
                        value={domain3}
                        onChange={(e) => setDomain3(e.target.value)}
                        data-testid="domain3-input"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleCompareDomains}
                    disabled={loadingComparison || !domain1.trim() || !domain2.trim()}
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 w-full md:w-auto"
                    data-testid="compare-domains-btn"
                  >
                    {loadingComparison ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing Competitors...</>
                    ) : "Run Competitor Analysis"}
                  </Button>

                  {/* Progress indicator */}
                  {loadingComparison && (
                    <div className="mt-4">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-orange-500 to-red-500 animate-pulse" style={{width: '60%'}}></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Fetching and analyzing domains... This may take up to 30 seconds.</p>
                    </div>
                  )}
                </Card>

                {/* Results Section */}
                {comparisonResults && (
                  <>
                    {/* Quick Insights Bar */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                        <p className="text-3xl font-bold text-blue-700">{comparisonResults.insights?.your_score || 0}</p>
                        <p className="text-xs text-blue-600 font-medium">Your Score</p>
                      </Card>
                      <Card className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                        <p className="text-3xl font-bold text-orange-700">{comparisonResults.insights?.avg_competitor_score || 0}</p>
                        <p className="text-xs text-orange-600 font-medium">Competitor Avg</p>
                      </Card>
                      <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                        <p className="text-3xl font-bold text-green-700">{comparisonResults.insights?.total_gap_keywords || 0}</p>
                        <p className="text-xs text-green-600 font-medium">Gap Keywords</p>
                      </Card>
                      <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                        <p className="text-3xl font-bold text-purple-700">{comparisonResults.insights?.easy_keywords_count || 0}</p>
                        <p className="text-xs text-purple-600 font-medium">Easy Wins</p>
                      </Card>
                      <Card className="p-4 bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
                        <p className="text-3xl font-bold text-pink-700">{comparisonResults.insights?.backlink_opportunities || 0}</p>
                        <p className="text-xs text-pink-600 font-medium">Backlink Opps</p>
                      </Card>
                      <Card className="p-4 bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
                        <p className="text-3xl font-bold text-cyan-700">{(comparisonResults.insights?.potential_traffic || 0).toLocaleString()}</p>
                        <p className="text-xs text-cyan-600 font-medium">Traffic Potential</p>
                      </Card>
                    </div>

                    {/* Tab Navigation */}
                    <Card className="p-2">
                      <div className="flex flex-wrap gap-2">
                        {['comparison', 'keywords', 'backlinks', 'pages', 'technical', 'recommendations'].map((tab) => (
                          <Button
                            key={tab}
                            variant={competitorTab === tab ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCompetitorTab(tab)}
                            className={competitorTab === tab ? "bg-orange-600 hover:bg-orange-700" : ""}
                          >
                            {tab === 'comparison' && 'Domain Comparison'}
                            {tab === 'keywords' && 'Keyword Gap'}
                            {tab === 'backlinks' && 'Backlink Gap'}
                            {tab === 'pages' && 'Top Pages'}
                            {tab === 'technical' && 'Technical'}
                            {tab === 'recommendations' && 'AI Insights'}
                          </Button>
                        ))}
                      </div>
                    </Card>

                    {/* Domain Comparison Tab */}
                    {competitorTab === 'comparison' && (
                      <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-orange-600" />
                          Domain Comparison
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Domain</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">Overall</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">SEO</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">Performance</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">Technical</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">SSL</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold">Issues</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {comparisonResults.comparison?.map((result, idx) => (
                                <tr key={idx} className={`hover:bg-gray-50 ${result.is_yours ? 'bg-blue-50' : ''}`}>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      {result.is_yours && <Badge className="bg-blue-600 text-white text-xs">YOU</Badge>}
                                      <div>
                                        <p className="font-medium text-sm">{result.domain}</p>
                                        {result.status === 'error' && (
                                          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3" />
                                            {result.error}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  {result.status === 'success' ? (
                                    <>
                                      <td className="px-4 py-3 text-center">
                                        <span className={`text-2xl font-bold ${getScoreColor(result.overall_score)}`}>
                                          {result.overall_score}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        <span className={`text-lg font-semibold ${getScoreColor(result.seo_score)}`}>{result.seo_score}</span>
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        <span className={`text-lg font-semibold ${getScoreColor(result.performance_score)}`}>{result.performance_score}</span>
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        <span className={`text-lg font-semibold ${getScoreColor(result.technical_score)}`}>{result.technical_score}</span>
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        {result.has_ssl ? (
                                          <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto" />
                                        ) : (
                                          <AlertTriangle className="w-5 h-5 text-red-600 mx-auto" />
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        <Badge variant={result.issues_count > 3 ? "destructive" : "secondary"}>
                                          {result.issues_count}
                                        </Badge>
                                      </td>
                                    </>
                                  ) : (
                                    <td colSpan="6" className="px-4 py-3 text-center">
                                      <span className="text-red-600 text-sm">{result.error || 'Analysis failed'}</span>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    )}

                    {/* Keyword Gap Tab */}
                    {competitorTab === 'keywords' && (
                      <Card className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <Target className="w-5 h-5 text-orange-600" />
                            Keyword Gap Analysis
                          </h3>
                          <div className="flex gap-2">
                            <Badge className="bg-green-100 text-green-700">Easy: {comparisonResults.easy_keywords?.length || 0}</Badge>
                            <Badge className="bg-blue-100 text-blue-700">High Vol: {comparisonResults.high_volume_keywords?.length || 0}</Badge>
                          </div>
                        </div>

                        {/* Easy Win Keywords */}
                        {comparisonResults.easy_keywords?.length > 0 && (
                          <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                            <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                              <Zap className="w-4 h-4" /> Easy Win Keywords (Difficulty &lt; 40)
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {comparisonResults.easy_keywords?.slice(0, 8).map((kw, idx) => (
                                <Badge key={idx} className="bg-green-600 text-white">
                                  {kw.keyword} ({kw.search_volume?.toLocaleString()})
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Full Keyword Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-orange-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Keyword</th>
                                <th className="px-3 py-2 text-center">Volume</th>
                                <th className="px-3 py-2 text-center">Difficulty</th>
                                <th className="px-3 py-2 text-center">Intent</th>
                                <th className="px-3 py-2 text-center">Comp. Pos</th>
                                <th className="px-3 py-2 text-center">CPC</th>
                                <th className="px-3 py-2 text-center">Traffic Pot.</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {comparisonResults.gap_keywords?.slice(0, 15).map((kw, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 font-medium">{kw.keyword}</td>
                                  <td className="px-3 py-2 text-center">{kw.search_volume?.toLocaleString()}</td>
                                  <td className="px-3 py-2 text-center">
                                    <Badge className={getDifficultyColor(kw.difficulty)}>{kw.difficulty}</Badge>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <Badge variant="outline" className="text-xs capitalize">{kw.intent}</Badge>
                                  </td>
                                  <td className="px-3 py-2 text-center text-green-600 font-semibold">
                                    #{kw.competitor_position}
                                  </td>
                                  <td className="px-3 py-2 text-center">${kw.cpc}</td>
                                  <td className="px-3 py-2 text-center text-purple-600 font-medium">
                                    {kw.traffic_potential?.toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    )}

                    {/* Backlink Gap Tab */}
                    {competitorTab === 'backlinks' && (
                      <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Link2 className="w-5 h-5 text-orange-600" />
                          Backlink Gap - Sites Linking to Competitors
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-orange-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Source Domain</th>
                                <th className="px-3 py-2 text-center">Domain Authority</th>
                                <th className="px-3 py-2 text-center">Link Type</th>
                                <th className="px-3 py-2 text-center">Linking to # Competitors</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {comparisonResults.backlink_gap?.map((bl, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-3 py-2">
                                    <div>
                                      <p className="font-medium">{bl.source_name}</p>
                                      <p className="text-xs text-gray-500">{bl.source_domain}</p>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <Badge className={bl.domain_authority > 80 ? "bg-green-600" : bl.domain_authority > 60 ? "bg-yellow-600" : "bg-gray-600"}>
                                      DA {bl.domain_authority}
                                    </Badge>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <Badge variant={bl.link_type === 'dofollow' ? "default" : "secondary"}>
                                      {bl.link_type}
                                    </Badge>
                                  </td>
                                  <td className="px-3 py-2 text-center text-orange-600 font-semibold">
                                    {bl.linking_to_competitors}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-xs text-gray-500 mt-4">
                          These high-authority sites link to your competitors but not you. Reach out with valuable content to earn backlinks.
                        </p>
                      </Card>
                    )}

                    {/* Top Competitor Pages Tab */}
                    {competitorTab === 'pages' && (
                      <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <FileText className="w-5 h-5 text-orange-600" />
                          Top Competitor Pages by Traffic
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-orange-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Competitor</th>
                                <th className="px-3 py-2 text-left">Page</th>
                                <th className="px-3 py-2 text-center">Est. Traffic</th>
                                <th className="px-3 py-2 text-left">Top Keyword</th>
                                <th className="px-3 py-2 text-center"># Keywords</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {comparisonResults.top_competitor_pages?.map((page, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 font-medium">{page.competitor}</td>
                                  <td className="px-3 py-2 text-blue-600">{page.page}</td>
                                  <td className="px-3 py-2 text-center font-semibold text-green-600">
                                    {page.estimated_traffic?.toLocaleString()}
                                  </td>
                                  <td className="px-3 py-2">{page.top_keyword}</td>
                                  <td className="px-3 py-2 text-center">{page.keywords_count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    )}

                    {/* Technical Comparison Tab */}
                    {competitorTab === 'technical' && (
                      <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Settings className="w-5 h-5 text-orange-600" />
                          Technical SEO Comparison
                        </h3>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="p-4 bg-blue-50 rounded-lg">
                            <h4 className="font-medium text-blue-800 mb-3">Your Domain</h4>
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span>SSL Certificate</span>
                                {comparisonResults.tech_comparison?.your_domain?.ssl ? 
                                  <CheckCircle2 className="w-5 h-5 text-green-600" /> : 
                                  <AlertTriangle className="w-5 h-5 text-red-600" />}
                              </div>
                              <div className="flex justify-between items-center">
                                <span>Mobile Friendly</span>
                                {comparisonResults.tech_comparison?.your_domain?.mobile_friendly ? 
                                  <CheckCircle2 className="w-5 h-5 text-green-600" /> : 
                                  <AlertTriangle className="w-5 h-5 text-red-600" />}
                              </div>
                              <div className="flex justify-between items-center">
                                <span>Schema Markup</span>
                                {comparisonResults.tech_comparison?.your_domain?.schema ? 
                                  <CheckCircle2 className="w-5 h-5 text-green-600" /> : 
                                  <AlertTriangle className="w-5 h-5 text-red-600" />}
                              </div>
                              <div className="flex justify-between items-center">
                                <span>Load Time</span>
                                <Badge className={comparisonResults.tech_comparison?.your_domain?.load_time < 2 ? "bg-green-600" : "bg-yellow-600"}>
                                  {comparisonResults.tech_comparison?.your_domain?.load_time}s
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="p-4 bg-orange-50 rounded-lg">
                            <h4 className="font-medium text-orange-800 mb-3">Competitor Average</h4>
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span>SSL Adoption</span>
                                <Badge>{comparisonResults.tech_comparison?.competitor_avg?.ssl_percentage}%</Badge>
                              </div>
                              <div className="flex justify-between items-center">
                                <span>Mobile Friendly</span>
                                <Badge>{comparisonResults.tech_comparison?.competitor_avg?.mobile_percentage}%</Badge>
                              </div>
                              <div className="flex justify-between items-center">
                                <span>Schema Usage</span>
                                <Badge>{comparisonResults.tech_comparison?.competitor_avg?.schema_percentage}%</Badge>
                              </div>
                              <div className="flex justify-between items-center">
                                <span>Avg Load Time</span>
                                <Badge>{comparisonResults.tech_comparison?.competitor_avg?.avg_load_time}s</Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    )}

                    {/* AI Recommendations Tab */}
                    {competitorTab === 'recommendations' && (
                      <Card className="p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-orange-600" />
                          AI-Powered Recommendations
                        </h3>
                        <div className="space-y-4">
                          {comparisonResults.recommendations?.map((rec, idx) => (
                            <div 
                              key={idx} 
                              className={`p-4 rounded-lg border-l-4 ${
                                rec.type === 'critical' ? 'bg-red-50 border-red-500' :
                                rec.type === 'important' ? 'bg-yellow-50 border-yellow-500' :
                                'bg-green-50 border-green-500'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                {rec.type === 'critical' && <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />}
                                {rec.type === 'important' && <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />}
                                {rec.type === 'opportunity' && <Lightbulb className="w-5 h-5 text-green-600 mt-0.5" />}
                                <div>
                                  <h4 className="font-semibold">{rec.title}</h4>
                                  <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                                  <p className="text-sm font-medium mt-2 text-gray-800">Action: {rec.action}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Audit History */}
            {activeTool === "audit-history" && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <History className="w-6 h-6 text-purple-700" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">SEO Audit History</h2>
                      <p className="text-gray-600">All your past website audits</p>
                    </div>
                  </div>
                  <Button variant="outline" onClick={fetchAuditHistory} disabled={loadingHistory}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingHistory ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {loadingHistory ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-3" />
                    <p className="text-gray-500">Loading audit history...</p>
                  </div>
                ) : auditHistory.length > 0 ? (
                  <div className="space-y-4">
                    {auditHistory.map((audit, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              audit.overall_score >= 70 ? 'bg-green-100' : 
                              audit.overall_score >= 50 ? 'bg-orange-100' : 'bg-red-100'
                            }`}>
                              <span className={`text-xl font-bold ${
                                audit.overall_score >= 70 ? 'text-green-600' : 
                                audit.overall_score >= 50 ? 'text-orange-600' : 'text-red-600'
                              }`}>
                                {audit.overall_score}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{audit.domain || audit.url}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(audit.analyzed_at).toLocaleDateString()}
                                </span>
                                <span>SEO: {audit.seo_score}</span>
                                <span>Perf: {audit.performance_score}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {audit.issues && (
                              <Badge variant={audit.issues.length > 5 ? "destructive" : "secondary"}>
                                {audit.issues.length} issues
                              </Badge>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setAuditUrl(audit.url);
                                setActiveTool("site-audit");
                                toast.info("Run a new audit to compare");
                              }}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Quick Issues Preview */}
                        {audit.issues && audit.issues.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="flex flex-wrap gap-2">
                              {audit.issues.slice(0, 3).map((issue, i) => (
                                <span key={i} className={`text-xs px-2 py-1 rounded ${getSeverityColor(issue.severity)}`}>
                                  {issue.issue || issue.category}
                                </span>
                              ))}
                              {audit.issues.length > 3 && (
                                <span className="text-xs text-gray-500">+{audit.issues.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <History className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Audit History Yet</h3>
                    <p className="text-gray-500 mb-4">Run your first SEO audit to see it here</p>
                    <Button onClick={() => setActiveTool("site-audit")}>
                      <Search className="w-4 h-4 mr-2" />
                      Run Site Audit
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {/* Keyword History Section */}
            {activeTool === "keyword-history" && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Keyword Search History</h2>
                      <p className="text-gray-500">Your recent keyword researches</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchKeywordHistory}
                    disabled={loadingKeywordHistory}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingKeywordHistory ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {loadingKeywordHistory ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
                  </div>
                ) : keywordHistory.length > 0 ? (
                  <div className="space-y-3">
                    {keywordHistory.map((item, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-4 bg-white border rounded-xl hover:shadow-md transition-all group"
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                            <Search className="w-5 h-5 text-violet-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{item.keyword}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" />
                                {item.search_volume?.toLocaleString() || 'N/A'} vol
                              </span>
                              <span>Difficulty: {item.difficulty || 'N/A'}%</span>
                              <span className="text-green-600">${item.cpc || '0'} CPC</span>
                              <span className="flex items-center gap-1 text-gray-400">
                                <Clock className="w-3 h-3" />
                                {new Date(item.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setKeyword(item.keyword);
                              setActiveTool("keyword-research");
                            }}
                          >
                            <Search className="w-4 h-4 mr-1" />
                            Research Again
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => deleteKeywordFromHistory(item.keyword)}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-600 mb-2">No Keyword History</h3>
                    <p className="text-gray-500 mb-4">Your keyword searches will appear here after you research keywords</p>
                    <Button 
                      onClick={() => setActiveTool("keyword-research")}
                      className="bg-violet-600 hover:bg-violet-700"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Start Keyword Research
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {/* Keyword Comparison Tool */}
            {activeTool === "keyword-compare" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Keyword Comparison</h2>
                    <p className="text-gray-500">Compare up to 3 keywords side-by-side</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-blue-500 to-indigo-500 text-white">NEW</Badge>
                </div>

                {/* Input Fields */}
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {[0, 1, 2].map((idx) => (
                    <div key={idx}>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Keyword {idx + 1} {idx < 2 && <span className="text-red-500">*</span>}
                      </label>
                      <Input
                        placeholder={`e.g., ${['digital marketing', 'seo services', 'content marketing'][idx]}`}
                        value={compareKeywords[idx]}
                        onChange={(e) => {
                          const newKeywords = [...compareKeywords];
                          newKeywords[idx] = e.target.value;
                          setCompareKeywords(newKeywords);
                        }}
                        data-testid={`compare-keyword-${idx}`}
                      />
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={handleKeywordComparison}
                  disabled={loadingCompare}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 mb-6"
                  data-testid="compare-keywords-btn"
                >
                  {loadingCompare ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Comparing Keywords...</>
                  ) : (
                    <><BarChart3 className="w-4 h-4 mr-2" /> Compare Keywords</>
                  )}
                </Button>

                {/* Comparison Results */}
                {compareResults.length > 0 && (
                  <div className="space-y-6">
                    {/* Overview Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="p-3 text-left font-semibold text-gray-700 border">Metric</th>
                            {compareResults.map((result, idx) => (
                              <th key={idx} className={`p-3 text-center font-semibold border ${result.success ? 'text-gray-700' : 'text-red-500'}`}>
                                {result.keyword}
                                {!result.success && <span className="block text-xs text-red-400">Error</span>}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="p-3 border font-medium bg-gray-50">Search Volume</td>
                            {compareResults.map((result, idx) => (
                              <td key={idx} className="p-3 border text-center">
                                {result.success ? (
                                  <span className="font-bold text-violet-600">
                                    {result.data?.keyword_metrics?.search_volume?.toLocaleString() || 'N/A'}
                                  </span>
                                ) : '-'}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="p-3 border font-medium bg-gray-50">Difficulty</td>
                            {compareResults.map((result, idx) => (
                              <td key={idx} className="p-3 border text-center">
                                {result.success ? (
                                  <Badge className={
                                    result.data?.keyword_metrics?.difficulty < 30 ? 'bg-green-100 text-green-700' :
                                    result.data?.keyword_metrics?.difficulty < 60 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }>
                                    {result.data?.keyword_metrics?.difficulty || 'N/A'}%
                                  </Badge>
                                ) : '-'}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="p-3 border font-medium bg-gray-50">CPC</td>
                            {compareResults.map((result, idx) => (
                              <td key={idx} className="p-3 border text-center">
                                {result.success ? (
                                  <span className="font-semibold text-green-600">
                                    ${result.data?.keyword_metrics?.cpc || '0'}
                                  </span>
                                ) : '-'}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="p-3 border font-medium bg-gray-50">Competition</td>
                            {compareResults.map((result, idx) => (
                              <td key={idx} className="p-3 border text-center">
                                {result.success ? (
                                  <span>{Math.round((result.data?.keyword_metrics?.competition || 0) * 100)}%</span>
                                ) : '-'}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="p-3 border font-medium bg-gray-50">Trend</td>
                            {compareResults.map((result, idx) => (
                              <td key={idx} className="p-3 border text-center">
                                {result.success ? (
                                  <Badge className={
                                    result.data?.keyword_metrics?.trend === 'rising' ? 'bg-green-100 text-green-700' :
                                    result.data?.keyword_metrics?.trend === 'declining' ? 'bg-red-100 text-red-700' :
                                    'bg-gray-100 text-gray-700'
                                  }>
                                    {result.data?.keyword_metrics?.trend === 'rising' ? '↑ Rising' :
                                     result.data?.keyword_metrics?.trend === 'declining' ? '↓ Declining' : '→ Stable'}
                                  </Badge>
                                ) : '-'}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="p-3 border font-medium bg-gray-50">Related Keywords</td>
                            {compareResults.map((result, idx) => (
                              <td key={idx} className="p-3 border text-center">
                                {result.success ? (
                                  <span>{result.data?.related_searches?.length || 0}</span>
                                ) : '-'}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="p-3 border font-medium bg-gray-50">Long-Tail Keywords</td>
                            {compareResults.map((result, idx) => (
                              <td key={idx} className="p-3 border text-center">
                                {result.success ? (
                                  <span>{result.data?.long_tail_keywords?.length || 0}</span>
                                ) : '-'}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Recommendation */}
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Lightbulb className="w-5 h-5" />
                        Recommendation
                      </h4>
                      <p className="text-blue-700 text-sm">
                        {(() => {
                          const successResults = compareResults.filter(r => r.success);
                          if (successResults.length === 0) return "Unable to compare - please try again.";
                          
                          // Find best keyword based on opportunity score (high volume, low difficulty)
                          const scored = successResults.map(r => ({
                            keyword: r.keyword,
                            score: (r.data?.keyword_metrics?.search_volume || 0) / Math.max((r.data?.keyword_metrics?.difficulty || 50), 1)
                          }));
                          const best = scored.reduce((a, b) => a.score > b.score ? a : b);
                          
                          return `Based on the volume-to-difficulty ratio, "${best.keyword}" appears to be the best opportunity for targeting. It offers a good balance between search demand and ranking difficulty.`;
                        })()}
                      </p>
                    </div>
                  </div>
                )}

                {compareResults.length === 0 && !loadingCompare && (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
                    <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-600 mb-2">Compare Keywords</h3>
                    <p className="text-gray-500">Enter at least 2 keywords above to see a side-by-side comparison</p>
                  </div>
                )}
              </Card>
            )}

            {/* Keyword Clustering Tool */}
            {activeTool === "keyword-cluster" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Keyword Clustering</h2>
                    <p className="text-gray-500">Group related keywords by topic for better content organization</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-orange-500 to-amber-500 text-white">NEW</Badge>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Enter Keywords (one per line, minimum 5)
                    </label>
                    <Textarea
                      placeholder="digital marketing tips&#10;seo strategies&#10;content marketing&#10;social media marketing&#10;email marketing&#10;ppc advertising&#10;marketing automation&#10;..."
                      value={clusterKeywords}
                      onChange={(e) => setClusterKeywords(e.target.value)}
                      className="min-h-[200px] font-mono text-sm"
                      data-testid="cluster-keywords-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {clusterKeywords.split('\n').filter(k => k.trim()).length} keywords entered
                    </p>
                  </div>

                  <Button
                    onClick={handleKeywordClustering}
                    disabled={loadingCluster}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
                    data-testid="cluster-keywords-btn"
                  >
                    {loadingCluster ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Clustering...</>
                    ) : (
                      <><Target className="w-4 h-4 mr-2" /> Create Clusters</>
                    )}
                  </Button>
                </div>

                {/* Cluster Results */}
                {clusterResults && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-orange-800">
                          {clusterResults.total_clusters} Clusters Created
                        </p>
                        <p className="text-sm text-orange-600">
                          From {clusterResults.total_keywords} keywords
                        </p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      {clusterResults.clusters.map((cluster, idx) => (
                        <div key={idx} className="p-4 border rounded-xl bg-white hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-gray-900">{cluster.name}</h4>
                            <div className="flex gap-2">
                              <Badge variant="outline">{cluster.count} keywords</Badge>
                              <Badge className={
                                cluster.intent === 'Transactional' ? 'bg-green-100 text-green-700' :
                                cluster.intent === 'Informational' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }>{cluster.intent}</Badge>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {cluster.keywords.slice(0, 8).map((kw, kidx) => (
                              <span key={kidx} className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded">
                                {kw}
                              </span>
                            ))}
                            {cluster.keywords.length > 8 && (
                              <span className="text-xs text-gray-400">+{cluster.keywords.length - 8} more</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!clusterResults && !loadingCluster && (
                  <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                    <h4 className="font-medium text-orange-800 mb-2">How Keyword Clustering Works</h4>
                    <ul className="text-sm text-orange-700 space-y-1">
                      <li>• Enter related keywords from your research</li>
                      <li>• Algorithm groups them by common terms and topics</li>
                      <li>• Each cluster can become a content pillar or page</li>
                      <li>• Intent detection helps prioritize commercial keywords</li>
                    </ul>
                  </div>
                )}
              </Card>
            )}

            {/* Industry Keywords Engine */}
            {activeTool === "industry-keywords" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Industry Keywords Engine</h2>
                    <p className="text-gray-500">AI-powered SEO blueprint for your industry</p>
                  </div>
                  <Badge className="ml-auto bg-gradient-to-r from-emerald-500 to-teal-500 text-white">AI Powered</Badge>
                </div>

                {/* Input Form */}
                <form onSubmit={handleIndustryKeywords} className="space-y-4 mb-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Industry *</label>
                      <Input
                        placeholder="e.g., Digital Marketing Agency, Real Estate, Healthcare"
                        value={industryForm.industry}
                        onChange={(e) => setIndustryForm({...industryForm, industry: e.target.value})}
                        data-testid="industry-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">City (Optional)</label>
                      <Input
                        placeholder="e.g., Mumbai, Delhi, Bangalore"
                        value={industryForm.city}
                        onChange={(e) => setIndustryForm({...industryForm, city: e.target.value})}
                        data-testid="industry-city-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Target Audience (Optional)</label>
                      <Input
                        placeholder="e.g., Small Businesses, Startups, Enterprise"
                        value={industryForm.target_audience}
                        onChange={(e) => setIndustryForm({...industryForm, target_audience: e.target.value})}
                        data-testid="industry-audience-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Business Type</label>
                      <select
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={industryForm.business_type}
                        onChange={(e) => setIndustryForm({...industryForm, business_type: e.target.value})}
                        data-testid="industry-type-select"
                      >
                        <option value="Local">Local Business</option>
                        <option value="National">National Business</option>
                        <option value="Global">Global Business</option>
                      </select>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    disabled={industryLoading}
                    data-testid="industry-generate-btn"
                  >
                    {industryLoading ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating Blueprint (30-60 sec)...</>
                    ) : (
                      <><Target className="w-4 h-4 mr-2" /> Generate SEO Blueprint</>
                    )}
                  </Button>
                </form>

                {/* Results */}
                {industryResults && (
                  <div className="space-y-6">
                    {/* Summary Header */}
                    <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-emerald-800">{industryResults.industry}</h3>
                          <p className="text-sm text-emerald-600">
                            {industryResults.city && `📍 ${industryResults.city} • `}
                            {industryResults.business_type} Business • 
                            Generated {new Date(industryResults.generated_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            const dataStr = JSON.stringify(industryResults, null, 2);
                            const blob = new Blob([dataStr], {type: 'application/json'});
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${industryResults.industry.replace(/\s+/g, '_')}_keywords.json`;
                            a.click();
                          }}
                        >
                          <Download className="w-4 h-4 mr-2" /> Export JSON
                        </Button>
                      </div>
                    </div>

                    <Tabs defaultValue="commercial" className="w-full">
                      <TabsList className="grid grid-cols-6 mb-4">
                        <TabsTrigger value="commercial" className="text-xs">Commercial</TabsTrigger>
                        <TabsTrigger value="informational" className="text-xs">Informational</TabsTrigger>
                        <TabsTrigger value="local" className="text-xs">Local SEO</TabsTrigger>
                        <TabsTrigger value="buyer" className="text-xs">Buyer Intent</TabsTrigger>
                        <TabsTrigger value="clusters" className="text-xs">Clusters</TabsTrigger>
                        <TabsTrigger value="strategy" className="text-xs">Strategy</TabsTrigger>
                      </TabsList>

                      {/* Commercial Keywords */}
                      <TabsContent value="commercial">
                        <div className="overflow-x-auto border rounded-xl">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-violet-50 to-purple-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-violet-800">#</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-violet-800">Keyword</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-violet-800">Intent</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-violet-800">Priority</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-violet-800">Content Type</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y bg-white">
                              {industryResults.commercial_keywords?.map((kw, idx) => (
                                <tr key={idx} className="hover:bg-violet-50/50">
                                  <td className="px-4 py-2 text-gray-400 text-sm">{idx + 1}</td>
                                  <td className="px-4 py-2 font-medium text-gray-900">{kw.keyword}</td>
                                  <td className="px-3 py-2 text-center">
                                    <Badge className="bg-purple-100 text-purple-700">{kw.intent}</Badge>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`font-bold ${kw.priority >= 8 ? 'text-green-600' : kw.priority >= 5 ? 'text-yellow-600' : 'text-gray-500'}`}>
                                      {kw.priority}/10
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-center text-sm text-gray-600">{kw.content_type}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </TabsContent>

                      {/* Informational Keywords */}
                      <TabsContent value="informational">
                        <div className="overflow-x-auto border rounded-xl">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800">#</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800">Keyword</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-blue-800">Intent</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-blue-800">Priority</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-blue-800">Content Type</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y bg-white">
                              {industryResults.informational_keywords?.map((kw, idx) => (
                                <tr key={idx} className="hover:bg-blue-50/50">
                                  <td className="px-4 py-2 text-gray-400 text-sm">{idx + 1}</td>
                                  <td className="px-4 py-2 font-medium text-gray-900">{kw.keyword}</td>
                                  <td className="px-3 py-2 text-center">
                                    <Badge className="bg-blue-100 text-blue-700">{kw.intent}</Badge>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`font-bold ${kw.priority >= 8 ? 'text-green-600' : kw.priority >= 5 ? 'text-yellow-600' : 'text-gray-500'}`}>
                                      {kw.priority}/10
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-center text-sm text-gray-600">{kw.content_type}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </TabsContent>

                      {/* Local SEO Keywords */}
                      <TabsContent value="local">
                        <div className="overflow-x-auto border rounded-xl">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-teal-50 to-emerald-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-teal-800">#</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-teal-800">Keyword</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-teal-800">Intent</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-teal-800">Priority</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-teal-800">Content Type</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y bg-white">
                              {industryResults.local_keywords?.map((kw, idx) => (
                                <tr key={idx} className="hover:bg-teal-50/50">
                                  <td className="px-4 py-2 text-gray-400 text-sm">{idx + 1}</td>
                                  <td className="px-4 py-2 font-medium text-gray-900">{kw.keyword}</td>
                                  <td className="px-3 py-2 text-center">
                                    <Badge className="bg-teal-100 text-teal-700">{kw.intent}</Badge>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`font-bold ${kw.priority >= 8 ? 'text-green-600' : kw.priority >= 5 ? 'text-yellow-600' : 'text-gray-500'}`}>
                                      {kw.priority}/10
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-center text-sm text-gray-600">{kw.content_type}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </TabsContent>

                      {/* Buyer Intent Keywords */}
                      <TabsContent value="buyer">
                        <div className="overflow-x-auto border rounded-xl">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-orange-50 to-amber-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-orange-800">#</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-orange-800">Keyword</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-orange-800">Intent</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-orange-800">Priority</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-orange-800">Content Type</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y bg-white">
                              {industryResults.buyer_intent_keywords?.map((kw, idx) => (
                                <tr key={idx} className="hover:bg-orange-50/50">
                                  <td className="px-4 py-2 text-gray-400 text-sm">{idx + 1}</td>
                                  <td className="px-4 py-2 font-medium text-gray-900">{kw.keyword}</td>
                                  <td className="px-3 py-2 text-center">
                                    <Badge className="bg-orange-100 text-orange-700">{kw.intent}</Badge>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`font-bold ${kw.priority >= 8 ? 'text-green-600' : kw.priority >= 5 ? 'text-yellow-600' : 'text-gray-500'}`}>
                                      {kw.priority}/10
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-center text-sm text-gray-600">{kw.content_type}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </TabsContent>

                      {/* Keyword Clusters */}
                      <TabsContent value="clusters">
                        <div className="grid md:grid-cols-2 gap-4">
                          {industryResults.keyword_clusters?.map((cluster, idx) => (
                            <Card key={idx} className="p-4 border-2 border-indigo-100 hover:border-indigo-300 transition-colors">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-bold text-indigo-800">{cluster.cluster_name}</h4>
                                <Badge variant="outline" className="text-indigo-600">{cluster.page_type}</Badge>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {cluster.keywords?.map((kw, i) => (
                                  <Badge key={i} className="bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    {kw}
                                  </Badge>
                                ))}
                              </div>
                            </Card>
                          ))}
                        </div>
                      </TabsContent>

                      {/* Strategy Tab */}
                      <TabsContent value="strategy" className="space-y-6">
                        {/* Quick Wins */}
                        <Card className="p-4 border-2 border-green-200 bg-green-50">
                          <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                            <Zap className="w-5 h-5" /> Quick Win Keywords
                          </h4>
                          <div className="grid md:grid-cols-2 gap-3">
                            {industryResults.quick_win_keywords?.map((kw, idx) => (
                              <div key={idx} className="p-3 bg-white rounded-lg border border-green-200">
                                <p className="font-medium text-gray-900">{kw.keyword}</p>
                                <p className="text-sm text-green-600">{kw.reason}</p>
                              </div>
                            ))}
                          </div>
                        </Card>

                        {/* Blog Titles */}
                        <Card className="p-4 border-2 border-blue-200 bg-blue-50">
                          <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                            <FileText className="w-5 h-5" /> Blog Title Ideas
                          </h4>
                          <div className="space-y-2">
                            {industryResults.blog_titles?.map((title, idx) => (
                              <div key={idx} className="p-3 bg-white rounded-lg border border-blue-200">
                                <p className="font-medium text-gray-900">{idx + 1}. {title}</p>
                              </div>
                            ))}
                          </div>
                        </Card>

                        {/* 90 Day Plan */}
                        {industryResults.ninety_day_action_plan && (
                          <Card className="p-4 border-2 border-purple-200 bg-purple-50">
                            <h4 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
                              <Activity className="w-5 h-5" /> 90-Day SEO Action Plan
                            </h4>
                            <div className="grid md:grid-cols-3 gap-4">
                              <div className="p-3 bg-white rounded-lg border border-purple-200">
                                <p className="font-bold text-purple-700 mb-2">Month 1</p>
                                <ul className="space-y-1 text-sm">
                                  {industryResults.ninety_day_action_plan.month_1?.map((action, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                                      {action}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="p-3 bg-white rounded-lg border border-purple-200">
                                <p className="font-bold text-purple-700 mb-2">Month 2</p>
                                <ul className="space-y-1 text-sm">
                                  {industryResults.ninety_day_action_plan.month_2?.map((action, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                                      {action}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="p-3 bg-white rounded-lg border border-purple-200">
                                <p className="font-bold text-purple-700 mb-2">Month 3</p>
                                <ul className="space-y-1 text-sm">
                                  {industryResults.ninety_day_action_plan.month_3?.map((action, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                                      {action}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </Card>
                        )}
                      </TabsContent>
                    </Tabs>
                  </div>
                )}
              </Card>
            )}

            {/* Real Data Dashboard */}
            {activeTool === "real-data" && (
              <Card className="p-6">
                <RealSEODashboard />
              </Card>
            )}
          </div>
        </div>
        
        {/* Related Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <RelatedTools currentToolId={activeTool} category="seo" />
        </div>
      </div>
      <ToolSEOContent toolId="keyword-research" />
      <Footer />
    </div>
  );
}
